import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { einheitFuer, T, useT, type Uebersetzer } from "../i18n";
import { Erklaerung, Knopf, Punkt, euro } from "./index";
import { Deckblatt } from "./Deckblatt";
import { Nachtraeglichfrage } from "./Nachtraeglichfrage";
import { nachtraeglichSchicken } from "../daten/nachtraeglich";
import { Blattvonunten } from "./Blattvonunten";
import { Abschnitt, Leersatz, Wertzeile, Zeilenblatt, Zweizeiler } from "./Handyformen";
import { Zeichen } from "./Zeichen";
import { Kundenwahl } from "./Kundenwahl";
import { OrtAnlegen, Ortblatt, ortsatzVon } from "./OrtAnlegen";
import { anschriftVon } from "./Ortsformular";
import { Zeitraum, type Spanne } from "./Zeitraum";
import { Feiertagshinweis, useFeiertageIm } from "./Feiertage";
import { Fruehabholungshinweis } from "./Randtag";
import { feiertagssatz } from "../daten/feiertage";
import { tagMitWochentag } from "./preistext";
import { Anbauteile } from "./Anbauteile";
import { ReservierteStuecke } from "./Reserviert";
import { pflichtOhneStueck } from "./ZubehoerWahl";
/* Das Blatt zum Posten — am Telefon öffnet es die Zeile im Abschluss, wie in
   der Sammlung (`MerklisteFab.tsx`). Aufgerufen, nicht nachgebaut. */
import { Postenblatt } from "./Postenblatt";
import { bedienerPflicht } from "@mrmachine/fach";
/* `tagMitWochentag` von dort und nicht aus `./preistext`: Die Zeile „Für
   alle Posten" schreibt „Mo 21.9.", und die Postenzeilen darunter sollen
   nicht „Mo 21.09." schreiben — zwei Schreibweisen in einem Blatt lesen sich
   wie zwei Daten. */
import {
  Zeitraumblatt, tagMitWochentag as tagKurzMitWochentag, tageZwischen, zeitraumText,
} from "../masken/handy/Zeitraumblatt";
import { useRuf } from "../schale/Ruf";
import { useTelefon } from "../schale/geraet";
import { Fehler } from "../daten/klient";
import {
  useKunde, useKunden, useLeistungsarten, useMerklisteAngebot, useMerklisteVermieten, usePreis,
  useTextbausteine, useZubehoerAuswahl,
} from "../daten/haken";
import { leistungssatz } from "../daten/leistungssatz";
import { heuteTag, tagKurz, useNachschlagen, type Typenlage } from "../daten/nachschlagen";
// Die Notiz an ein Angebot, dessen Reservierung gefallen ist (18.09.2026).
import { notizAnlegen } from "../daten/routen";
import { useNamen } from "./Merkliste";
import { ARTWORT, Bausteinzugriff, fehlerWerte } from "./Positionen";
import { mengeFolgtAusAufmass, mengentext } from "./Leistungsmenge";
import { Postenaufstellung, Posteneinstellungen, Postenteil } from "./Postenaufstellung";
import { Gespann } from "./Gespann";
import { Laengensatz, textZuLang } from "./Laengensatz";
import {
  fehlendePreise, abgeschicktEntfernen, alsMerkzuschlag, bedienerFehlt, gehoertZu, istMiete, postenAendernWennFrei, postenSumme, schonGezaehltJePosten, useMerkliste, zuschlaegeZusammenfuehren, zuschlagNetto, zuschlagsteile, type Merkposten, type Teilposten,
} from "../daten/merkliste";
// Der Schalter „Kunde holt ab" — derselbe Baustein wie in der Sammlung (0127).
import { Abholung, AbholungAbweichung, AbholungKopf, useAbholungFuerAlle } from "./Abholung";
import {
  abHofHoltAb, abholungAllerStand, abholungAusPreis, abholungGegenPflichtPosten, abholungKopfNach, abholungKopfVorgabe,
  abholungMoeglich,
} from "../daten/abholung";
import { frageNachAbschlussLeeren, useVermietstand } from "../daten/vermietstand";
import {
  folgenDemGemeinsamen, gemeinsamerMietzeitraum, weichtAb, zeitraumEinzeln, zeitraumWieAlle,
} from "../daten/eigenerzeitraum";
import type { Bausteinart, Belegart, Einsatzort, Kunde } from "../daten/typen";
import { abschlussBefund } from "../daten/reserviertsatz";
import { usePreisfrageMelden, usePreiseUnterwegs } from "../daten/preisfrage";
import { ladestandVon, leersatzFuer } from "../daten/ladestand";

/* ══════════════════════════════════════════════════════════════════
   Der Abschluss der Merkliste — vom Warenkorb zum Vorgang.

   Luca, 17.09.2026: *„Unten rechts in der Ecke habe ich generell zwei
   Buttons: jetzt vermieten und Angebot erstellen. Das ist auf Basis der
   gemerkten Artikel, und dann kommt die Abfrage: welcher Kunde, welche
   Baustelle. Wenn man den Kunden noch nicht hat, legt man ihn in diesem
   Flow an. Bei Vermieten legt man direkt den Auftrag an — da muss man
   nochmal bestätigen, der Kunde hat telefonisch bestellt. Oder man
   erzeugt das Angebot, und das geht vorausgefüllt auf. Zwischenschritt
   ist, dass man den Zeitraum final klärt und prüft, ob es verfügbar ist
   und wann."*

   **Ein Weg für beide Knöpfe, bis zum letzten Schritt.** Kunde,
   Einsatzort, Zeitraum, Verfügbarkeit — vier Abschnitte, in dieser
   Reihenfolge, weil jeder den nächsten möglich macht: Ohne Kunden gibt es
   keine Einsatzorte, ohne Zeitraum keine Verfügbarkeit. Erst ganz unten
   trennen sich die beiden.

   **Kein Assistent mit Vor und Zurück.** Alle vier stehen untereinander
   auf einem Blatt, und was noch nicht dran ist, steht grau da. Am Telefon
   nennt der Anrufer die Dinge selten in der Reihenfolge, in der ein
   Assistent sie fragt — wer den Zeitraum korrigieren will, nachdem der
   Kunde steht, soll dafür nicht zweimal auf „Zurück" drücken.

   **Was nicht frei ist, blockiert nicht stumm.** Die Zeile sagt es, und
   daneben stehen die drei Auswege: Posten herausnehmen, Zeitraum ändern,
   oder trotzdem weiter als Angebot — eine Option blockt nicht, und eine
   Vormerkung bleibt eine Vormerkung (Regel 6).

   **Gerechnet wird neu, nicht aus der Liste** (Regel 4). Die Merkliste
   trägt den Preis vom Augenblick der Auskunft; hier wird er noch einmal
   geholt — mit dem Kunden, also mit seiner Kondition. Weicht er ab, steht
   das an der Zeile und nicht im Kleingedruckten.

   ── Am Telefon ein Blatt von unten in Handyformen (20.09.2026, abends) ──

   Luca: *„Auch wenn ich etwas direkt vermieten will, ist das mit dem ‚für
   wen', ‚wohin' und ‚wann' und ‚es ist frei' … nicht optimal gemacht. Das
   müsstest du auch noch mal überarbeiten, damit es in unser konsistentes
   Mobile-Design reinpasst."* Und: *„Die Baustelle kann ich auch nicht
   auswählen, also das fehlt auch komplett."*

   **Der Befund auf 390 × 844:** Das Deckblatt des Schreibtischs kam als
   Blatt von unten, und darin stand die Schreibtischmaske — fünf nummerierte
   Abschnitte in Saira, der Kunde als Anker mit „Ändern", die Einsatzorte als
   Kacheln mit gestrichelter Kante, das Kalenderfeld des Schreibtischs mit
   „5 Tage ›", je Posten eine gerahmte Karte mit Kreuz, Kalenderfeld,
   Häkchen „Kunde holt ab" und „● 1 von 1 frei", ein Aufklapper „▸ TEXTE UND
   HINWEISE", eine Häkchenzeile und zwei Schreibtischknöpfe (44 px). Nichts
   davon war eine Form der Handyfassung; die Baustellenkachel war anzutippen,
   sah aber nicht danach aus.

   **Jetzt:** ein `Blattvonunten` (`gross`), darin vier Abschnitte in den
   Formen aus `Handyformen.tsx` — und die Reihenfolge ist Lucas Satz:

   | | Form | dahinter |
   |---|---|---|
   | **Für wen, wohin, wann** | ein `Zeilenblatt`, drei `Zweizeiler`, darunter die Bestellnummer als Feld (08.10.2026) | das Kundenblatt der `Kundenwahl` (über `anker`), das `Ortblatt` mit „Einsatzort anlegen" als letzter Zeile (`OrtAnlegen.tsx`), das `Zeitraumblatt` für „Wann?" |
   | **Posten — und ist es frei?** | je Posten eine Zeile mit **Ampel** | je Posten das `Postenblatt` (Zeitraum, Menge, Zubehör, Abholung) |
   | **Was noch?** | Hofhinweis und interne Notiz als Felder, Vor- und Nachtext als Zeilen | ein Blatt mit dem einen Text, Bausteine darüber |
   | **Und dann?** | die Summe als `Wertzeile`, die Bestätigung als Schalterzeile | die beiden Knöpfe im Fuß des Blattes |

   **„Es ist frei" ist der Punkt an der Zeile** — grün, offener Ring oder rot,
   und daneben der Satz („1 von 1 frei", „nur 1 frei, 2 gebraucht", „nichts
   frei — wieder ab Mo 28.9."). Es ist dieselbe Auskunft wie an der
   Trefferzeile beim Vermieten und in der Sammlung (`befundVon`, unverändert)
   — eine Aussage über die **Belegung**, keine über den Preis (Regel 6); der
   steht rechts, in Mono, für sich.

   **Was am Telefon nicht in der Zeile steht, steht im Blatt zum Posten:**
   Zeitraum je Posten, Menge, „Kunde holt ab", Anbauteile, Kreuz. Die Zeile
   öffnet den Datensatz (28-designkonzept.md) — ein Kreuz und ein Häkchen
   neben der Fläche, die öffnet, wären die zwei Ziele in einer Zeile, die die
   Sammlung am 19.09.2026 schon einmal losgeworden ist. Gerechnet und
   gesperrt wird genau wie am Schreibtisch: dieselben Haken, dieselben
   Riegel (`Postenzeile`), nur anders gezeichnet.

   Am Schreibtisch bleibt das Deckblatt — seit dem 08.10.2026 mit demselben
   Kopf (für wen · wohin · wann · Bestellnr.) als Formular im Raster.

   Klassen: `ma-*` und am Telefon `mat-*` (`stile/merkliste.css`), gegen alle
   Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

export type Abschlussart = "vermieten" | "angebot";

function fehlerPfad(e: unknown): string {
  return e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
}

/** Zwei Cent Abstand sind kein anderer Preis — zwei Euro schon. */
const anders = (a: number, b: number): boolean => Math.abs(a - b) >= 0.005;

/** Der Schlüssel eines Zeitfensters. Posten mit demselben werden zusammen gefragt. */
const fensterVon = (p: Merkposten): string => `${p.von}/${p.bis}`;

/**
 * **Wohin eine Fehlermeldung zeigt** (Systemprüfung Befund 43, 25.09.2026).
 * Lisa: *„Die Fehlermeldung erschien, aber das betroffene Feld selbst war
 * nicht farblich hervorgehoben."* Die Meldung stand unten über den Knöpfen,
 * und was sie meinte, stand drei Zeilen weiter oben ohne Zeichen. Jetzt
 * trägt die Stelle, an der es klemmt, den roten Rahmen — solange es dort
 * klemmt.
 */
type Fehlerstelle = "bediener" | "pflicht" | "reserviert" | "preis";

/** Was an einem Posten steht, wenn die Lage bekannt ist. */
type Befund = {
  art: "gut" | "acht" | "stopp";
  satz: string;
};

function befundVon(t: Uebersetzer, p: Merkposten, lage: Typenlage | undefined): Befund | null {
  if (!lage) return null;
  if (lage.bestand === 0) return { art: "stopp", satz: t("lage.kein_geraet") };
  if (lage.frei >= p.menge) {
    return {
      art: "gut",
      satz: p.menge === 1
        ? t("merkliste.frei", { frei: lage.frei, bestand: lage.bestand })
        : t("merkliste.freiGenug", { frei: lage.frei, menge: p.menge }),
    };
  }
  /* Frei nur, weil ein Angebot es hält (B-08): kein Halt, aber ein Handgriff
     — „Trotzdem nehmen" direkt unter dem Satz (seit N3 an der Zeile, nicht
     mehr nur im Blatt zum Posten). Wer das getan hat, liest es. */
  /* Angebot oder Auftrag im Abschluss: „Trotzdem nehmen" darunter. Wird es
     gerade vermietet, steht kein Knopf da — und der Satz sagt das (N7-05). */
  const vorgemerkt = abschlussBefund(lage, p.menge, p.reservierungUebergehen === true);
  if (vorgemerkt) return { art: vorgemerkt.art, satz: t(vorgemerkt.schluessel) };
  if (lage.frei > 0) {
    return { art: "acht", satz: t("merkliste.freiZuWenig", { frei: lage.frei, menge: p.menge }) };
  }
  return {
    art: "stopp",
    satz: lage.freiAb
      ? t("merkliste.erstFreiAb", { tag: tagMitWochentag(t, lage.freiAb) })
      : t("merkliste.nichtsFrei"),
  };
}

/* ══════════════ Eine Zeile ══════════════ */

function Postenzeile({
  p, name, kundeNr, lage, entfernen, aendern, melden, pflichtMelden, reserviertMelden, hervor = null,
  telefon = false, oeffnen, nicht, auf = false, setzeAuf, gemeinsam, abholKopf = false,
}: {
  p: Merkposten;
  /** „Kunde holt ab" im Kopf — weicht dieser Posten ab, sagt die Zeile es (08.10.2026). */
  abholKopf?: boolean;
  /**
   * Aufgeklappt — **vom Abschluss gehalten, nicht von der Zeile** (08.10.2026).
   * Die Zeilen stehen je Zeitfenster in einer eigenen Liste (`Fenster`); wer
   * einem Posten einen eigenen Zeitraum gibt, schiebt ihn in ein anderes
   * Fenster, und die Zeile entsteht dort neu. Mit dem Zustand in der Zeile
   * klappte sie dabei zu — mitten im Ändern.
   */
  auf?: boolean;
  setzeAuf?: ((id: string, an: boolean) => void) | undefined;
  /** Der Zeitraum, der für alle gilt — weicht dieser Posten ab, sagt die Zeile es. */
  gemeinsam: Spanne;
  /** Die Codes „je Auftrag", die schon an einem anderen Posten zählen (V-20). */
  nicht?: ReadonlySet<string> | undefined;
  /** Der Name der Zeile — aus dem Posten, sonst aus dem Typenstamm. Leer,
   *  solange der Stamm unterwegs ist. */
  name: string;
  kundeNr: string;
  lage: Typenlage | undefined;
  entfernen: (id: string) => void;
  aendern: (id: string, teil: Teilposten) => void;
  /** Was diese Zeile gerade wirklich kostet — die Summe unten rechnet damit. */
  melden: (id: string, betrag: number) => void;
  /** Welches Pflichtzubehör im Zeitraum kein freies Stück hat. */
  pflichtMelden: (id: string, fehlt: string[]) => void;
  /** Ob hier ein nur reserviertes Stück steht, das noch niemand genommen hat (Befund 43). */
  reserviertMelden: (id: string, offen: boolean) => void;
  /** Worauf die Meldung unten zeigt, wenn sie diesen Posten meint (Befund 43). */
  hervor?: Fehlerstelle | null;
  /** Am Telefon: eine Zeile der Handyfassung mit Ampel, die das Blatt zum Posten öffnet. */
  telefon?: boolean;
  oeffnen?: ((p: Merkposten) => void) | undefined;
}) {
  const t = useT();
  const klappen = (an: boolean) => setzeAuf?.(p.id, an);
  /** Ein eigener Zeitraum, abweichend vom gemeinsamen (08.10.2026). */
  const eigen = istMiete(p) && weichtAb(p, gemeinsam);

  /* ── Das Pflichtzubehör ────────────────────────────────────────────
     **Gefragt wird hier und nicht im Blatt am Posten** (18.09.2026). Der
     Wert `pflichtFehlt` entsteht in `ZubehoerWahl` — und die steht nur im
     Postenblatt, das die meisten nie aufmachen. Ein Träger ohne freies
     Pflichtstück ging damit durch (docs/38-vermietmodul.md, Punkt 2).

     Eine Abfrage je Mietzeile, mit demselben Schlüssel wie im Blatt: Wer
     dort war, bezahlt sie nicht zweimal.

     **Den Träger selbst prüft der Server** (`annahme.kein_geraet`, seit dem
     18.09.2026); das Pflichtzubehör lässt er absichtlich offen, weil es sich
     an einer bestehenden Position nicht nachwählen lässt. Genau diese Lücke
     steht hier — und sie hat einen Weg heraus: das Blatt am Posten, ein
     anderer Zeitraum, oder der Weg als Angebot (Regel 6 und 10). */
  const { data: zubehoerlage } = useZubehoerAuswahl(p.typId || undefined, p.von, p.bis);
  const pflichtFehlt = useMemo(() => pflichtOhneStueck(zubehoerlage), [zubehoerlage]);
  useEffect(() => { pflichtMelden(p.id, pflichtFehlt); }, [pflichtMelden, p.id, pflichtFehlt]);

  /* **Der Preis wird geholt, nicht gelesen** (Regel 4). Mit dem Kunden:
     Wer eine Kondition hat, sieht hier seine Zahl und nicht die der Liste. */
  const auskunft = usePreis(
    p.typId, p.von, p.bis, kundeNr || null, p.bedient, p.varianteId || undefined,
    { halten: true },
  );
  const preis = auskunft.data;

  /* Der Zeitraum ist hier änderbar (Luca: *„Zwischenschritt ist, dass man den
     Zeitraum final klärt"*). Wer ihn ändert, ändert damit auch den Preis —
     und dann ist der gemerkte Betrag kein Vergleich mehr, sondern ein Rest.
     Also wird er übernommen, sobald die neue Antwort da ist. */
  const gewechselt = useRef(false);
  /* **Ein Posten ohne Betrag bekommt seinen hier.** Er kommt aus dem
     Verfügbarkeitscheck, wo nach einem Gerät gefragt wird und nicht nach
     einem Preis (Luca, 17.09.2026). Spätestens beim Abschluss muss die Zahl
     stehen — und sie steht dann auch in der Leiste, wenn jemand abbricht und
     weitersucht. */
  /* Und ein Posten, dessen Zeitraum anderswo neu gesetzt wurde — „für alle
     Posten" oben in diesem Schirm etwa (Systemprüfung A-06). */
  const nachtragen = !p.gerechnet || p.zeitraumNeu === true;
  /* Solange der Betrag noch kommt, steht unten keine Summe (N12-01). */
  usePreisfrageMelden(p.id, nachtragen && istMiete(p) && Boolean(p.typId) && !auskunft.isError);
  useEffect(() => {
    if ((!gewechselt.current && !nachtragen) || !preis || auskunft.isPlaceholderData) return;
    gewechselt.current = false;
    /* Was von Hand abgewählt oder zugesagt ist, bleibt — dieselbe
       Zusammenführung wie in `merken` und in der Sammelkarte. */
    const zusammen = zuschlaegeZusammenfuehren(
      p.zuschlaege,
      preis.zuschlaege.filter((z) => z.vorgewaehlt).map(alsMerkzuschlag),
    );
    aendern(p.id, {
      gerechnet: true,
      miettage: preis.miettage,
      netto: preis.netto,
      /* **Die Einheiten gehören dazu** — `mietsatz` rechnet einen zugesagten
         Satz mit ihnen hoch. Ohne sie stünde nach einem Zeitraumwechsel der
         neue Satz an der alten Menge. */
      einheiten: preis.menge,
      einheitKey: preis.einheit_key,
      /* Und die Summe der Zuschläge folgt derselben Liste: Ein abgewählter
         zählt mit 0,00, sonst wäre der Rückfallwert größer als die Zeilen. */
      zuschlaegeNetto: zusammen.reduce((s, z) => s + zuschlagNetto(z), 0),
      zuschlaege: zusammen,
      /* **Auf diese Pauschale schlägt bewusst nichts auf** (0144). Der
         Abschluss holt den Preis noch einmal — und muss die Aussage
         mitnehmen, sonst steht ein zuschlagsfreier Posten hier plötzlich
         wieder wie einer da, für den noch niemand gerechnet hat. Dieselbe
         Zeile wie in `bausteine/Postenblatt.tsx`. */
      zuschlagsfrei: preis.zuschlagsfrei === true,
      /* Abholung und Gespann aus derselben Antwort — sonst fehlt der Schalter
         „Kunde holt ab" an einem Posten, der ohne Preis kam (Systemprüfung
         A-02). */
      ...abholungAusPreis(preis),
      zeitraumNeu: undefined,
      // Der Name aus dem Stamm bleibt stehen, sonst müsste ihn der Abschluss
      // beim nächsten Mal wieder nachschlagen — und die Position braucht ihn.
      ...(p.bezeichnung || !name ? {} : { bezeichnung: name, typ: p.typ || name }),
    });
  }, [preis, auskunft.isPlaceholderData, aendern, p.id, nachtragen, name, p.bezeichnung, p.typ]);

  /* **Frisch gerechnet — und was ein Mensch daran entschieden hat, bleibt
     stehen.** Bis zum 18.09.2026 nahm diese Zeile die vorgewählten Zuschläge
     roh aus der Antwort: Ein abgewählter stand hier wieder drin, und der
     Abschluss war um genau ihn teurer als die Sammlung. Lisas Durchgang:
     *„Beim Merken 557,00, jetzt 572,00"* — die Anlieferung, die der Schalter
     „Kunde holt ab" weggenommen hatte (Migration 0127). Dieselbe
     Zusammenführung wie oben und wie in `merken`; eine zweite Rechenart für
     dieselbe Zeile war die ganze Abweichung.

     **Und der zugesagte Satz schlägt den gerechneten** — wie `mietsatz` am
     Posten. Ein Preis, den jemand am Telefon genannt hat, ist kein anderer,
     nur weil der Abschluss ihn noch einmal holt (Regel 4 gilt der
     Preisfindung, nicht der Zusage). */
  const zuschlaege = useMemo(
    () => zuschlaegeZusammenfuehren(
      p.zuschlaege,
      (preis?.zuschlaege ?? []).filter((z) => z.vorgewaehlt).map(alsMerkzuschlag),
    ),
    [p.zuschlaege, preis],
  );
  const mieteJetzt = preis
    ? (p.einzelpreis === undefined
      ? preis.netto
      : Math.round(p.einzelpreis * preis.menge * 100) / 100)
    : null;
  /* **Die Anbauteile mit eigenem Tarif zählen mit** (Migration 0078,
     18.09.2026). Sie werden drüben eigene Zeilen unter dieser Miete; ohne sie
     stünde hier weniger, als der Auftrag danach trägt. Gerechnet werden sie
     nicht hier, sondern von `bausteine/Anbauteile.tsx` — der Wert steht am
     Posten, wie ihn auch `postenSumme` liest. */
  /* **Die Anlieferung einmal je Auftrag** (V-20-Nebenbefund, 03.10.2026):
     nicht je Stück und nicht an jedem Posten — wie der Server und die AB. */
  const teile = zuschlagsteile(zuschlaege, nicht);
  const jetztJeStueck = mieteJetzt === null
    ? null
    : mieteJetzt + teile.jeStueck + (p.anbauNetto ?? 0);
  const jetzt = jetztJeStueck === null ? null : Math.round((jetztJeStueck * p.menge + teile.einmal) * 100) / 100;
  const gemerkt = postenSumme(p, nicht);
  /* Nur wo ein gemerkter Betrag DASTAND, kann er abweichen. Ein Posten aus
     dem Verfügbarkeitscheck hat keinen — „beim Merken 0,00" wäre ein Satz
     über etwas, das nie behauptet wurde. */
  const abweichung = jetzt !== null && p.gerechnet && !gewechselt.current && anders(jetzt, gemerkt);

  /* **Die Summe unten ist die Summe der Zeilen** — und die Zeilen rechnen
     mit dem frisch geholten Preis. Bis eben stand unten die Summe aus der
     Merkliste: Wer einen Posten von heute Morgen liegen hatte, las oben
     407,50 und unten 999,00. Zwei Zahlen fuer dieselbe Sache sind eine zu
     viel — und die falsche ist die, die niemand geholt hat. */
  useEffect(() => { if (jetzt !== null) melden(p.id, jetzt); }, [jetzt, melden, p.id]);

  const befund = befundVon(t, p, lage);
  /* **Frei nur, weil ein Angebot es hält — dann „Trotzdem nehmen" hier**
     (Systemprüfung N3, 24.09.2026). Bis dahin stand an der Zeile nur der
     Satz, und der Knopf saß im Blatt zum Posten, das die meisten nie
     öffnen. Das Stück dazu fragt `ReservierteStuecke` erst, wenn der Fall
     da ist — sonst kostet die Zeile keine zweite Abfrage. */
  const vorgemerkt = lage && lage.bestand > 0 ? abschlussBefund(lage, p.menge, p.reservierungUebergehen === true) : null;
  const reserviertFall = vorgemerkt !== null;
  /* Der Abschluss muss wissen, wo „Trotzdem nehmen" noch offen ist — sonst
     kann die Meldung nach dem Klick nicht darauf zeigen (Befund 43). */
  const reserviertOffen = reserviertFall && vorgemerkt?.nehmbar === true && p.reservierungUebergehen !== true;
  useEffect(() => { reserviertMelden(p.id, reserviertOffen); }, [reserviertMelden, p.id, reserviertOffen]);
  /** Die Klasse an der Stelle, auf die die Meldung zeigt — leer, wenn sie woanders hinzeigt. */
  const rot = (stelle: Fehlerstelle): string => (hervor === stelle ? " fehlerhaft" : "");

  /* ── Am Telefon: die Zeile der Handyfassung ──────────────────────
     Der Punkt ist die Ampel der Belegung (Regel 6) — und was den Auftrag
     aufhält, steht in derselben Spalte: kein freies Pflichtstück ist rot,
     der fehlende Bediener ein offener Ring (ein Mensch muss ran, das Angebot
     geht trotzdem). Rechts der frisch geholte Betrag, in Mono. Die
     Anbauteile rechnen darunter weiter wie am Schreibtisch — sie schreiben
     ihren Betrag an den Posten, und den braucht die Summe. */
  if (telefon) {
    const trenn = ` ${t("allgemein.trennpunkt")} `;
    const ohneBediener = bedienerFehlt(p);
    const pflichtwas = pflichtFehlt[0];
    const art = pflichtwas ? "stopp" : ohneBediener ? "acht" : befund?.art ?? "ruhig";
    const lagesatz = pflichtwas
      ? t("zubehoer.pflichtFehltKnopf", { zubehoer: pflichtwas })
      : ohneBediener
        ? t("merkliste.bedienerOffen")
        : befund?.satz ?? t("allgemein.laedt");
    const unter = [
      t("vermietenTelefon.spanne", { von: tagKurzMitWochentag(t, p.von), bis: tagKurzMitWochentag(t, p.bis) }),
      eigen ? t("merkliste.eigenerZeitraum") : "",
      p.menge > 1 ? t("merkliste.stueckzahl", { zahl: p.menge }) : "",
      lagesatz,
      abweichung ? t("merkliste.preisAnders", { alt: euro(gemerkt), neu: euro(jetzt ?? 0) }) : "",
    ].filter(Boolean).join(trenn);
    return (
      <>
        <Zweizeiler
          {...(oeffnen ? { beiKlick: () => oeffnen(p) } : {})}
          punkt={art}
          titel={name || t("merkliste.ohneNamen")}
          unter={unter}
          satz={Boolean(pflichtwas) || ohneBediener || abweichung}
          rechts={jetzt === null ? t("allgemein.laedt") : euro(jetzt)}
        />
        <AbholungAbweichung p={p} kopf={abholKopf} telefon />
        {reserviertFall ? (
          <ReservierteStuecke p={p} aendern={aendern} klasse={`ma-reserviert telefon${rot("reserviert")}`} />
        ) : null}
        <Anbauteile p={p} kunde={kundeNr} />
      </>
    );
  }

  /* ── Am Schreibtisch: eine Zeile, die aufklappt (08.10.2026) ──────────
     Luca: *„Wenn ich ‚Jetzt vermieten' mache, kann ich auch nicht in die
     Details der einzelnen Positionen gehen. Das geht offensichtlich nur über
     die Seitenleiste."* Jetzt klappt jede Zeile auf und zeigt dieselben
     Einstellungen und dieselbe Aufstellung wie die Merkliste
     (`Postenaufstellung.tsx`) — gerechnet mit der frisch geholten Antwort,
     damit Kopf und Tafel dieselbe Zahl nennen. Was den Auftrag aufhält
     (Lage, Bediener, Pflichtstück, Preis), steht weiter offen an der Zeile. */
  const name_ = name || t("merkliste.ohneNamen");
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const miettage = preis?.miettage ?? p.miettage;
  const unten = [
    t("angebotBauen.artMiete"),
    t("merkliste.stueckzahl", { zahl: p.menge }),
    p.von && p.bis ? t("zeitfrage.vonBis", { von: tagKurz(p.von), bis: tagKurz(p.bis) }) : "",
    eigen ? t("merkliste.eigenerZeitraum") : "",
    miettage ? (miettage === 1 ? t("preisauskunft.einMiettag") : t("preisauskunft.miettage", { zahl: miettage })) : "",
    p.bedient && p.bediener ? p.bediener : "",
  ].filter(Boolean).join(trenn);

  return (
    <li
      className={`ma-zeile${befund ? ` ${befund.art}` : ""}${hervor ? " fehlerhaft" : ""}${auf ? " auf" : ""}`}
      data-fehlerstelle={hervor ?? undefined}
    >
      <div className="ma-kopf">
        <button
          type="button"
          className="ma-griff"
          aria-expanded={auf}
          aria-label={t("merkliste.aufstellung.aufklappen", { was: name_ })}
          onClick={() => klappen(!auf)}
        >
          <b className="ma-name">{name_}</b>
          <b className="ma-betrag">{jetzt === null ? t("allgemein.laedt") : euro(jetzt)}</b>
          <span className="ma-unten">{unten}</span>
          {/* Ein Feiertag im Zeitraum des Postens (0287) — er zählt als Miettag. */}
          <Feiertagshinweis von={p.von} bis={p.bis} miettag klasse="ml-feiertag" />
          <Fruehabholungshinweis von={p.von} klasse="ml-feiertag" />
          <span className={`ml-ecke${auf ? " offen" : ""}`} aria-hidden="true"><Zeichen name="weiter" /></span>
        </button>
        {/* Weicht er von „Kunde holt ab" im Kopf ab, steht es hier — ein Klick macht ihn wie alle. */}
        <AbholungAbweichung p={p} kopf={abholKopf} />
        <button
          type="button"
          className="ml-weg"
          aria-label={t("merkliste.entfernenSchild", { was: name_ })}
          title={t("merkliste.entfernenSchild", { was: name_ })}
          onClick={() => entfernen(p.id)}
        >
          <Zeichen name="kreuz" />
        </button>
      </div>

      {/* Die Lage im gefragten Fenster — dieselbe Rechnung wie beim
          Nachschlagen (`GET /nachschlagen`). */}
      <div className="ma-lage">
        {befund ? (
          <>
            <Punkt art={befund.art} />
            <span>{befund.satz}</span>
          </>
        ) : (
          <span className="ma-still">{t("allgemein.laedt")}</span>
        )}
      </div>

      {/* **Wer fährt — an der Zeile, nicht erst nach dem Klick** (Systemprüfung
          E-02, 24.09.2026). Steht er nicht fest, sagt es die Zeile, und der
          Knopf daneben öffnet das Blatt zum Posten. */}
      {bedienerPflicht(p.bedienungspflicht) || (p.bedient && !p.bediener) ? (
        <p className={`ma-bediener${rot("bediener")}`}>
          <Punkt art={p.bediener ? "gut" : "acht"} />
          <span>
            {p.bediener
              ? t("merkliste.bedienerIst", { name: p.bediener })
              : t("merkliste.bedienerOffenKurz")}
          </span>
          {oeffnen ? (
            <Knopf klein onClick={() => oeffnen(p)}>
              {t(p.bediener ? "merkliste.bedienerAendern" : "merkliste.bedienerWaehlen")}
            </Knopf>
          ) : null}
        </p>
      ) : null}

      {/* Abgeholt, obwohl das Gerät nur mit Bediener hinausgeht — offen, nicht verboten (wie im Angebot). */}
      {abholungGegenPflichtPosten(p) ? (
        <p className="ma-bediener">
          <Punkt art="acht" />
          <span>{t("positionen.pflichtGegenAbholungSatz")}</span>
        </p>
      ) : null}

      {reserviertFall ? (
        <ReservierteStuecke p={p} aendern={aendern} klasse={`ma-reserviert${rot("reserviert")}`} />
      ) : null}

      {/* **Was ohne freies Pflichtstück nicht hinausgeht** — an der Zeile,
          an der es klemmt. Es hält nicht auf: Das Angebot geht durch, nur die
          Vermietung nicht (Regel 6 und 10). */}
      {pflichtFehlt.map((was) => (
        <p className={`ma-pflichtteil${rot("pflicht")}`} key={was}>
          <Punkt art="stopp" />
          <span>{t("zubehoer.pflichtFehltKnopf", { zubehoer: was })}</span>
        </p>
      ))}

      {/* **Preis immer individuell, noch keiner genannt** (0227, E1). An der
          Zeile, an der es klemmt; eingetragen wird er in der Aufstellung
          darunter — der Knopf klappt sie auf. */}
      {fehlendePreise(p).map((z) => (
        <p className={`ma-pflichtteil${rot("preis")}`} key={`preis-${z.code}`}>
          <Punkt art="stopp" />
          <span>{t("merkliste.preisFehltZeile", { was: z.bezeichnung })}</span>
          {auf ? null : (
            <Knopf klein onClick={() => klappen(true)}>{t("merkliste.preisEintragen")}</Knopf>
          )}
        </p>
      ))}

      {abweichung ? (
        <p className="ma-anders">
          {t("merkliste.preisAnders", { alt: euro(gemerkt), neu: euro(jetzt ?? 0) })}
        </p>
      ) : null}

      {auf ? (
        <div className="ma-auf">
          <Posteneinstellungen
            p={p}
            /* **Ein eigener Zeitraum je Posten** (Luca, 08.10.2026: *„Wenn
               dort für alle Posten 9. bis 15. steht, kann ich das auf
               Positionsebene nicht verändern"*). Weicht er vom gemeinsamen ab,
               ist es seiner und folgt ihm nicht mehr; trifft er ihn, folgt er
               wieder. Das Feld meldet erst den ganzen Zeitraum (`ganz`). */
            zeitraumSetzen={(neu: Spanne) => {
              if (!neu.von || !neu.bis) return;
              gewechselt.current = true;
              aendern(p.id, zeitraumEinzeln({ von: neu.von, bis: neu.bis }, gemeinsam));
            }}
            zeitraumMarke={eigen ? (
              <span className="ma-eigen">
                <span>{t("merkliste.eigenerZeitraum")}</span>
                <button
                  type="button"
                  className="pst-verweis"
                  onClick={() => {
                    const zurueck = zeitraumWieAlle(gemeinsam);
                    if (!zurueck) return;
                    gewechselt.current = true;
                    aendern(p.id, zurueck);
                  }}
                >
                  {t("merkliste.wieAlle")}
                </button>
              </span>
            ) : undefined}
            stueckSetzen={(menge) => {
              /* Ein Stück mehr ist ein Gerät mehr — nur, wenn es frei ist. */
              if (menge > p.menge) void postenAendernWennFrei(p.id, { menge });
              else aendern(p.id, { menge });
            }}
            bedientSetzen={(bedient) => aendern(p.id, bedient ? { bedient: true } : { bedient: false, bediener: "" })}
            kopf={oeffnen ? (
              <button type="button" className="pst-verweis" onClick={() => oeffnen(p)}>
                {t("vermieten.blattOeffnen")}
              </button>
            ) : undefined}
          />
          <Postenaufstellung
            p={p}
            preis={preis}
            frisch
            nicht={nicht}
            aendern={(teil) => aendern(p.id, teil)}
          />
          {/* **„Kunde holt ab" steht auch hier** (Lisas Durchgang, 18.09.2026)
              — derselbe Baustein wie in der Merkliste (0127). */}
          {abholungMoeglich(p) || (Boolean(p.gespann) && !p.wegen) ? (
            <Postenteil titel={t("merkliste.detailsTransport")}>
              <Abholung p={p} />
              <Gespann p={p} />
            </Postenteil>
          ) : null}
        </div>
      ) : null}

      {/* Was als eigene Zeile mit hinausgeht — und was es kostet (0078). */}
      <Anbauteile p={p} kunde={kundeNr} />
    </li>
  );
}

/**
 * Die Menge einer weiteren Zeile in ihrer Einheit — leer bei einer Eins und
 * an einer Textzeile. **Eine Leistung, deren Menge aus dem Aufmaß folgt, sagt
 * das** (08.10.2026) statt „0 m²": Eine Null sähe aus wie gemessen.
 */
function mengeMarke(p: Merkposten, t: (k: string, w?: Record<string, string | number>) => string): string {
  if (p.art === "text" || p.menge === 1) return "";
  if (mengeFolgtAusAufmass(p)) return t("positionen.mengeAufmass");
  return p.einheitKey
    ? t("merkliste.mengeEinheit", { zahl: mengentext(p.menge), einheit: einheitFuer(t, p.einheitKey, p.menge) })
    : t("merkliste.stueckzahl", { zahl: p.menge });
}

/**
 * **Eine weitere Zeile — Leistung, Ware, Zuschlag, Freitext** (18.09.2026).
 *
 * Sie hat keine Lage und keine Belegung, also steht hier auch nichts davon:
 * Name, Art, Menge, Betrag — und an einer Leistung ihr Termin (P10-02). Ein Preis wird für sie nicht
 * geholt — er kommt aus dem Katalog oder aus dem Feld und ist damit schon
 * entschieden. Die eine Sorte Zeile, die Regel 4 nichts angeht.
 */
function Weiterezeile({ p, entfernen, aendern, telefon = false }: {
  p: Merkposten;
  entfernen: (id: string) => void;
  aendern: (id: string, teil: Teilposten) => void;
  /** Am Telefon: eine Zeile ohne Ziel — geändert wird in der Sammlung. */
  telefon?: boolean;
}) {
  const t = useT();
  const name = p.bezeichnung || t("merkliste.ohneNamen");
  /* **Der Grad einer Leistung** (P8-05, 05.10.2026). Führt die Leistungsart
     mehrere Preiskategorien — die PV-Reinigung drei Verschmutzungsgrade —,
     steht hier die Wahl; ohne Vorgabe ist sie Pflicht, bevor vermietet wird
     (Regel 11: nur, wo es nötig ist — ein einziger Grad oder eine Vorgabe
     gilt ohne Frage). Der Satz daneben ist der, den die Annahme einfriert. */
  const { data: leistungsarten } = useLeistungsarten();
  const leistung = p.art === "leistung" && p.leistungsartId
    ? (leistungsarten?.zeilen ?? []).find((l) => l.id === p.leistungsartId) ?? null
    : null;
  const satz = leistung ? leistungssatz(leistung, p.kategorieId ?? null, p.menge) : null;
  const [auf, setzeAuf] = useState(() => satz?.wahlNoetig === true);
  const gradwahl = leistung && satz && satz.grade.length > 1 ? (
    <label className="ma-grad">
      <span className="feld-schild">{t("merkliste.gradSchild")}</span>
      <select
        className={`feld fein${satz.wahlNoetig ? " fehlerhaft" : ""}`}
        aria-invalid={satz.wahlNoetig || undefined}
        value={p.kategorieId ?? satz.kategorieId ?? ""}
        onChange={(e) => {
          const neu = e.target.value || null;
          const jetzt = leistungssatz(leistung, neu, p.menge);
          aendern(p.id, { kategorieId: neu ?? undefined, netto: jetzt.satz ?? 0, einzelpreis: undefined });
        }}
      >
        {satz.wahlNoetig ? <option value="">{t("merkliste.gradWaehlen")}</option> : null}
        {satz.grade.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
      </select>
    </label>
  ) : null;
  /* **Der Termin einer Leistung** (P10-02, Durchgang 05.10.2026): Bis dahin
     stand sie hier ohne Tag, und der Auftrag zeigte danach „Termin offen".
     Jetzt steht der Tag aus „Vermieten" da und lässt sich ändern. */
  const termin = p.art !== "leistung"
    ? ""
    : p.von
      ? p.bis && p.bis !== p.von
        ? t("vermietenTelefon.spanne", { von: tagKurzMitWochentag(t, p.von), bis: tagKurzMitWochentag(t, p.bis) })
        : tagKurzMitWochentag(t, p.von)
      : t("merkliste.terminOffen");
  if (telefon) {
    const trenn = ` ${t("allgemein.trennpunkt")} `;
    const menge = mengeMarke(p, t);
    return (
      <>
        <Zweizeiler
          titel={name}
          unter={[t(ARTWORT[p.art] ?? ""), menge, termin].filter(Boolean).join(trenn)}
          rechts={p.art === "text" ? t("allgemein.strich") : euro(postenSumme(p))}
        />
        {gradwahl}
      </>
    );
  }
  /* ── Am Schreibtisch: aufklappbar wie die Mietzeile (08.10.2026) ──
     Aufgeklappt Termin, Menge, Grad bzw. Stück und dieselbe Aufstellung
     wie in der Merkliste. Fehlt der Grad, steht sie offen da — ohne ihn
     wird nicht vermietet (P8-05). Eine Textzeile hat nichts aufzuklappen. */
  const unten = [t(ARTWORT[p.art] ?? ""), mengeMarke(p, t) || (p.art === "text" ? "" : t("merkliste.stueckzahl", { zahl: mengentext(p.menge) })), termin]
    .filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);
  const textzeile = p.art === "text";
  return (
    <li className={`ma-zeile weitere${auf ? " auf" : ""}`}>
      <div className="ma-kopf">
        {textzeile ? (
          <span className="ma-griff ruhig">
            <b className="ma-name">{name}</b>
            <span className="ma-betrag">{t("allgemein.strich")}</span>
            <span className="ma-unten">{unten}</span>
          </span>
        ) : (
          <button
            type="button"
            className="ma-griff"
            aria-expanded={auf}
            aria-label={t("merkliste.aufstellung.aufklappen", { was: name })}
            onClick={() => setzeAuf((x) => !x)}
          >
            <b className="ma-name">{name}</b>
            <b className="ma-betrag">{euro(postenSumme(p))}</b>
            <span className="ma-unten">{unten}</span>
            <span className={`ml-ecke${auf ? " offen" : ""}`} aria-hidden="true"><Zeichen name="weiter" /></span>
          </button>
        )}
        <button
          type="button"
          className="ml-weg"
          aria-label={t("merkliste.entfernenSchild", { was: name })}
          title={t("merkliste.entfernenSchild", { was: name })}
          onClick={() => entfernen(p.id)}
        >
          <Zeichen name="kreuz" />
        </button>
      </div>
      {satz?.wahlNoetig ? (
        <p className="ma-pflichtteil">
          <Punkt art="acht" />
          <span>{t("merkliste.gradWaehlen")}</span>
        </p>
      ) : null}
      {auf && !textzeile ? (
        <div className="ma-auf">
          <Posteneinstellungen
            p={p}
            zeitraumSetzen={p.art === "leistung" ? (neu: Spanne) => {
              if (!neu.von || !neu.bis) return;
              aendern(p.id, { von: neu.von, bis: neu.bis });
            } : undefined}
            stueckSetzen={(menge) => aendern(p.id, { menge })}
          />
          <Postenaufstellung p={p} aendern={(teil) => aendern(p.id, teil)} />
        </div>
      ) : null}
    </li>
  );
}

/* ══════════════ Ein Zeitfenster ══════════════ */

/**
 * Alle Posten eines Fensters in **einer** Frage.
 *
 * `GET /nachschlagen` beantwortet eine Suche über viele Typen in drei
 * Abfragen; je Posten einmal `GET /verfuegbarkeit` wären so viele Aufrufe wie
 * Zeilen. Getrennt wird nur nach Zeitraum — zwei Fenster sind zwei Fragen.
 */
function Fenster({
  von, bis, posten, name, kundeNr, offen, entfernen, aendern, melden, pflichtMelden, reserviertMelden, hervor,
  telefon = false, oeffnen, nicht, aufe, setzeAuf, gemeinsam, abholKopf = false,
}: {
  /** „Kunde holt ab" im Kopf (08.10.2026). */
  abholKopf?: boolean;
  /** Welche Zeilen aufgeklappt sind — gehalten vom Abschluss (08.10.2026). */
  aufe?: ReadonlySet<string>;
  setzeAuf?: (id: string, an: boolean) => void;
  gemeinsam: Spanne;
  /** Je Posten die Codes „je Auftrag", die schon ein anderer trägt (V-20). */
  nicht: (p: Merkposten) => ReadonlySet<string> | undefined;
  von: string;
  bis: string;
  posten: Merkposten[];
  name: (p: Merkposten) => string;
  kundeNr: string;
  offen: boolean;
  entfernen: (id: string) => void;
  aendern: (id: string, teil: Teilposten) => void;
  melden: (id: string, betrag: number) => void;
  pflichtMelden: (id: string, fehlt: string[]) => void;
  reserviertMelden: (id: string, offen: boolean) => void;
  /** Je Posten, worauf die Meldung zeigt (Befund 43). */
  hervor: (id: string) => Fehlerstelle | null;
  /** Am Telefon stehen die Zeilen direkt im `Zeilenblatt` des Aufrufers — ohne eigene Liste. */
  telefon?: boolean;
  oeffnen?: ((p: Merkposten) => void) | undefined;
}) {
  const typen = useMemo(() => [...new Set(posten.map((p) => p.typId))], [posten]);
  const { data } = useNachschlagen({ wort: "", gruppe: null, von, bis, typen }, offen);
  const lagen = useMemo(
    () => new Map((data?.zeilen ?? []).map((z) => [z.typId, z])),
    [data],
  );

  const zeilen = posten.map((p) => (
    <Postenzeile
      key={p.id}
      p={p}
      name={name(p)}
      kundeNr={kundeNr}
      lage={data?.von === von && data.bis === bis ? lagen.get(p.typId) : undefined}
      entfernen={entfernen}
      aendern={aendern}
      melden={melden}
      pflichtMelden={pflichtMelden}
      reserviertMelden={reserviertMelden}
      hervor={hervor(p.id)}
      telefon={telefon}
      oeffnen={oeffnen}
      nicht={nicht(p)}
      auf={aufe?.has(p.id) ?? false}
      setzeAuf={setzeAuf}
      gemeinsam={gemeinsam}
      abholKopf={abholKopf}
    />
  ));

  if (telefon) return <>{zeilen}</>;
  return <ul className="ma-zeilen">{zeilen}</ul>;
}

/* ══════════════ Die Texte am Telefon ══════════════ */

/** Welcher Belegtext im Blatt steht. Bestellnummer, Hofhinweis und
 *  interne Notiz stehen seit dem 08.10.2026 direkt im Abschluss. */
type Textart = "vortext" | "nachtext";

/**
 * Eine Zeile je Text — was dasteht, oder was ohne Text auf den Beleg kommt.
 * Dieselbe Zeile wie die Texte des Angebots am Telefon
 * (`masken/vorgaenge/AngebotTexte.tsx`); der Standard steht hier als
 * Auskunft, weil „leer heißt Standard" gilt (Migration 0094/0105) und die
 * Zeile sonst „Noch kein Text" sagte, obwohl einer aufs Papier kommt.
 */
function Textzeile({ schild, art, beleg, wert, oeffnen }: {
  schild: string;
  art: Bausteinart;
  beleg: Belegart;
  wert: string;
  oeffnen: () => void;
}) {
  const t = useT();
  const abfrage = useTextbausteine(art, true, beleg);
  const standard = abfrage.data?.zeilen.find((b) => b.standard);
  const da = wert.trim();
  /* **Ob ein Standard aufs Papier kommt, sagt erst die Antwort** (N12-01):
     Vorher stand zwei Sekunden lang „Leer — es kommt nichts auf den Beleg",
     obwohl der Standard „Dank für den Auftrag" kommt. */
  const ohneText = standard
    ? t("merkliste.textLeerStandard", { stichwort: standard.stichwort })
    : t(leersatzFuer(ladestandVon(abfrage), "merkliste.textLeer"));
  return (
    <Zweizeiler
      beiKlick={oeffnen}
      titel={t(schild)}
      unter={da || ohneText}
    />
  );
}

/* ══════════════ Ein Belegtext mit seinen Bausteinen ══════════════ */

/**
 * Vor- oder Nachtext eines Belegs — mit **Wählen** und **Sichern** daneben.
 *
 * Luca, 16.09.2026: *„Wir wollen Freitexte auch während der Anlage speichern
 * können und dann den Schnellzugriff auf diese abgespeicherten Schnelltexte
 * haben."* Die alte Schnellerfassung hatte den Zugriff, der Abschluss nicht —
 * dort standen vier nackte Felder (docs/38-vermietmodul.md, Punkt 4).
 *
 * **Derselbe Baustein wie überall** (`bausteine/Positionen.tsx`,
 * `Bausteinzugriff`), dieselbe Liste aus den Stammdaten, dasselbe Sichern.
 * Zwei Wege zu denselben Sätzen wären zwei Listen, sobald einer ein Feld
 * bekommt.
 *
 * **Leer heißt Standard** — also steht der Standard im leeren Feld
 * (Migration 0094/0105). Wer ihn abtippt, tippt ihn umsonst.
 */
function Belegfeld({ id, art, beleg, schild, platz, wohin, was, wert, aendern }: {
  id: string;
  art: Bausteinart;
  /** Zu welchem Beleg der Satz gehört — er entscheidet die Liste (0105). */
  beleg: Belegart;
  schild: string;
  platz: string;
  /** Auf welchen Beleg er geht, als Satz daneben. */
  wohin?: string;
  /** Wenn die Beschriftung allein es nicht sagt. */
  was?: string;
  wert: string;
  aendern: (neu: string) => void;
}) {
  const t = useT();
  const { data: bausteine } = useTextbausteine(art, true, beleg);
  const standard = bausteine?.zeilen.find((b) => b.standard);

  return (
    <div className="ma-text">
      <div className="ma-text-kopf">
        {/* Kein `<label>` um das Ganze: Darin läge auch der Schnellzugriff,
            und jeder Klick auf „Wählen" spränge ins Textfeld. */}
        <label className="feld-schild" htmlFor={id}>
          {t(schild)}
          {was ? <Erklaerung k={was} /> : null}
        </label>
        {wohin ? <span className="ma-wohin">{t(wohin)}</span> : null}
        <Bausteinzugriff art={art} beleg={beleg} wert={wert} einsetzen={aendern} />
      </div>
      <textarea
        className="feld texte-feld"
        id={id}
        rows={2}
        aria-invalid={textZuLang(wert)}
        placeholder={standard ? standard.text : t(platz)}
        value={wert}
        onChange={(e) => aendern(e.target.value)}
      />
      {/* Zu lang: stehen lassen und sagen (N4-16), statt still zu kürzen. */}
      <Laengensatz text={wert} />
    </div>
  );
}

/* ══════════════ Das Blatt ══════════════ */

export function MerklisteAbschluss({ offen, art, schliessen, fertig }: {
  offen: boolean;
  /** Welcher Knopf gedrückt wurde. Er entscheidet nur den letzten Schritt. */
  art: Abschlussart;
  schliessen: () => void;
  /** Durch — die Liste ist leer, die Leiste darf sich schließen. */
  fertig: () => void;
}) {
  const t = useT();
  const ruf = useRuf();
  const gehZu = useNavigate();
  const { posten: alle, wer, setzeWer, aendern, entfernen } = useMerkliste();
  /* **Abgeschlossen wird, was für diesen Kunden gemerkt ist** (Lisa,
     24.09.2026). Was für einen anderen liegt, steht darunter und geht nur
     mit, wenn jemand es ausdrücklich übernimmt — dann neu gerechnet, denn
     der Preis war mit der Kondition des anderen gerechnet. */
  const posten = useMemo(() => alle.filter((p) => gehoertZu(p, wer.nr)), [alle, wer.nr]);
  /* Welcher Posten die Anlieferung „einmal je Auftrag" trägt (V-20-Nebenbefund). */
  const schonGezaehlt = useMemo(() => schonGezaehltJePosten(posten), [posten]);
  const nichtAn = useCallback((p: Merkposten) => schonGezaehlt.get(p), [schonGezaehlt]);
  const fremde = useMemo(() => alle.filter((p) => !gehoertZu(p, wer.nr)), [alle, wer.nr]);
  /* Der Name einer Zeile — aus dem Posten, sonst aus dem Typenstamm. Ein
     Posten aus dem Verfügbarkeitscheck darf ohne kommen, und die API weist
     eine Position ohne Bezeichnung ab. */
  const namen = useNamen(posten);

  /* **Kunde und Einsatzort stehen in der Sammlung, nicht in diesem Blatt.**
     Die Vermietfläche fragt sie oben (Luca, 17.09.2026: *„Für ‚Wen' muss
     einfach oben bei Gerät mit hin"*), das Blatt hier zeigt und ändert
     dieselben. Zwei Stände für denselben Kunden wären die Frage, welcher
     gilt — und die Antwort wäre „der, den man zuletzt angefasst hat".

     `kunde` ist der volle Satz aus dem Stamm, den die Auswahl braucht;
     gespeichert ist nur Nummer, Name und Art. */
  const { data: kundenliste } = useKunden();
  const kunde = useMemo(
    () => (kundenliste?.zeilen ?? []).find((k) => k.nr === wer.nr) ?? null,
    [kundenliste, wer.nr],
  );
  /* **Gewählt, aber nicht mehr wählbar** (K-17, 03.10.2026): Wurde der
     Kunde inzwischen deaktiviert, fällt er aus der Liste — und das Blatt
     stand mit „Kunde wählen …" da, ohne zu sagen, warum. Dann der Satz, den
     auch die API meldet (`kunde.deaktiviert`). */
  const kundeWeg = Boolean(wer.nr) && Boolean(kundenliste) && !kunde;
  const ort = wer.ort;
  const setzeKunde = (k: Kunde | null) => setzeWer({
    nr: k?.nr ?? "",
    name: k?.name ?? "",
    art: k?.art === "interessent" || k?.art === "vermieter" ? k.art : "kunde",
  });
  const setzeOrt = (neu: string) => setzeWer({ ort: neu });
  const [ortBlatt, setzeOrtBlatt] = useState<string | null>(null);
  const [bestaetigt, setzeBestaetigt] = useState(false);
  const [fehler, setzeFehler] = useState("");
  /* **Die Werte zum Satz** (Systemprüfung E-02/I-03, 24.09.2026). Bis hierher
     stand die Meldung ohne sie da: „<b>{person}</b> hat {qual} nicht" —
     wörtlich, mit spitzen Klammern und geschweiften. Jetzt setzt der Satz
     Name, Qualifikation und Datum ein und zeichnet das Fette fett. */
  const [fehlerDaten, setzeFehlerDaten] = useState<Record<string, string | number>>({});
  /** Der Fehler selbst — für die Rückfrage „nachträglich erfassen?" (F20). */
  const [fehlerRoh, setzeFehlerRoh] = useState<unknown>(null);
  /* „Ja, nachträglich erfassen" gilt bis zum Schluss — ein zweiter Versuch fragt nicht noch einmal (F20-Rest). */
  const [nachtraeglichJa, setzeNachtraeglichJa] = useState(false);
  /** Worauf die Meldung zeigt — die Stelle trägt dann den roten Rahmen (Befund 43). */
  const [stelle, setzeStelle] = useState<Fehlerstelle | null>(null);
  /* ── Was noch ──────────────────────────────────────────────────────
     Luca, 17.09.2026: *„Wenn ich direkt vermiete, will ich auch Texte
     erfassen können: für wen, wohin, wann ist es frei, gibt es weitere
     Infos?"* Drei Felder, alle freiwillig, alle eingeklappt (Regel 11) —
     der Regelfall am Telefon hat keinen Satz. */
  const [vortext, setzeVortext] = useState("");
  const [nachtext, setzeNachtext] = useState("");
  const [notiz, setzeNotiz] = useState("");
  /* **Die Bestellnummer des Kunden** (0188, Systemprüfung 14). Lisa: *„bei
     der Direktvermietung optional eintragbar"* — gewerbliche Kunden geben
     sie am Telefon durch. Sie geht als Kopfzeile auf AB, Lieferschein und
     Rechnung.

     **Sie und der Hinweis für den Hof stehen am Entwurf** (08.10.2026), wie
     Kunde und Ort: Wer den Abschluss schließt, weitersucht und wieder
     öffnet, findet sie noch — vorher lagen sie hier im `useState` und waren
     mit dem Fenster weg. */
  const bestellnummer = wer.bestellnummer;
  const setzeBestellnummer = (neu: string) => setzeWer({ bestellnummer: neu });
  const hofhinweis = wer.hofhinweis;
  const setzeHofhinweis = (neu: string) => setzeWer({ hofhinweis: neu });
  /** Die Belegtexte (Vor- und Nachtext) — zugeklappt, Luca: *„Die Texte kann man aufklappen."* */
  const [texteOffen, setzeTexteOffen] = useState(false);
  /** Welche Postenzeilen aufgeklappt sind — hier und nicht in der Zeile (siehe `Postenzeile`). */
  const [aufe, setzeAufe] = useState<ReadonlySet<string>>(new Set());
  const setzeAuf = useCallback((id: string, an: boolean) => {
    setzeAufe((alt) => {
      if (alt.has(id) === an) return alt;
      const neu = new Set(alt);
      if (an) neu.add(id); else neu.delete(id);
      return neu;
    });
  }, []);

  /* ── Die Blätter am Telefon ──────────────────────────────────────
     Vier Blätter, die über dem Abschluss aufgehen: die Einsatzorte, der
     Zeitraum für alle, das Blatt zum Posten und der eine Text. Alles
     flüchtig — ein Blatt, das beim nächsten Öffnen schon offen steht,
     erklärt niemandem, warum. */
  const telefon = useTelefon();
  const [orteOffen, setzeOrteOffen] = useState(false);
  const [zeitOffen, setzeZeitOffen] = useState(false);
  /** Welcher Posten sein Blatt offen hat — `null` heißt keiner. */
  const [blattPosten, setzeBlattPosten] = useState<string | null>(null);
  /** Welcher Text im Blatt steht, und sein Entwurf. */
  const [textOffen, setzeTextOffen] = useState<Textart | null>(null);
  const [feld, setzeFeld] = useState("");
  const textVon = (welcher: Textart): string => (welcher === "vortext" ? vortext : nachtext);
  const textSetzen = (welcher: Textart, neu: string) => {
    if (welcher === "vortext") setzeVortext(neu);
    else setzeNachtext(neu);
  };
  const textOeffnen = (welcher: Textart) => { setzeFeld(textVon(welcher)); setzeTextOffen(welcher); };
  const textUebernehmen = () => {
    if (textOffen) textSetzen(textOffen, feld.trim());
    setzeTextOffen(null);
  };
  /** Auf welchem Beleg Vor- und Nachtext landen — das entscheidet der Knopf. */
  const belegwort = art === "vermieten"
    ? "belegtexte.wohin.schnell"
    : "belegtexte.wohin.angebot";
  /* Dieselbe Auskunft als Wert: Sie entscheidet, **welche** Bausteine zur
     Wahl stehen und zu welcher Belegart ein hier gesicherter Satz gehört
     (Migration 0105). Beim Vermieten ist es die Auftragsbestätigung — die
     Direktvermietung hat kein Angebot (Regel 10). */
  const belegart: Belegart = art === "vermieten" ? "auftragsbestaetigung" : "angebot";
  /** Je Posten der frisch geholte Betrag — er schlaegt den gemerkten. */
  const [betraege, setzeBetraege] = useState<Record<string, number>>({});
  const melden = useCallback((id: string, betrag: number) => {
    setzeBetraege((alt) => (alt[id] === betrag ? alt : { ...alt, [id]: betrag }));
  }, []);
  /** Je Posten das Pflichtzubehör, von dem im Zeitraum keines frei ist. */
  const [pflichtJe, setzePflichtJe] = useState<Record<string, string[]>>({});
  const pflichtMelden = useCallback((id: string, fehlt: string[]) => {
    setzePflichtJe((alt) => {
      const da = alt[id] ?? [];
      if (da.length === fehlt.length && da.every((x, i) => x === fehlt[i])) return alt;
      return { ...alt, [id]: fehlt };
    });
  }, []);
  /** Je Posten: steht er nur reserviert da, ohne „Trotzdem nehmen"? (Befund 43) */
  const [reserviertJe, setzeReserviertJe] = useState<Record<string, boolean>>({});
  const reserviertMelden = useCallback((id: string, offen: boolean) => {
    setzeReserviertJe((alt) => (alt[id] === offen ? alt : { ...alt, [id]: offen }));
  }, []);

  const vermieten = useMerklisteVermieten();
  const angebot = useMerklisteAngebot();
  const laeuft = vermieten.isPending || angebot.isPending;

  /* Beim Öffnen fängt der Abschluss von vorn an. Was beim letzten Mal
     dastand, gehörte zu einem anderen Anruf. */
  useEffect(() => {
    if (!offen) return;
    setzeBestaetigt(false);
    setzeFehler("");
    setzeStelle(null);
    setzeBetraege({});
    setzePflichtJe({});
    setzeReserviertJe({});
    setzeOrteOffen(false);
    setzeZeitOffen(false);
    setzeBlattPosten(null);
    setzeTextOffen(null);
    setzeAufe(new Set());
    setzeTexteOffen(false);
  }, [offen]);

  /* Die Einsatzorte kommen aus der **Akte**, nicht aus der Liste: `GET /kunden`
     liefert unter `orte` eine Anzahl, keine Liste (siehe Schnellerfassung). */
  const { data: kundenakte } = useKunde(kunde?.nr ?? "");
  const orte = useMemo<Einsatzort[]>(
    () => (kundenakte?.orte ?? []).filter((o) => o.aktiv !== false),
    [kundenakte],
  );

  /* **Der Standardort wird gesetzt, nicht gefragt** (Regel 11) — einmal je
     Kunde. Wer ihn löscht, um einen anderen zu wählen, bekommt ihn nicht beim
     nächsten Zeichnen zurück. */
  const vorbelegtFuer = useRef("");

  useEffect(() => {
    const nr = kunde?.nr ?? "";
    if (!nr || !kundenakte || kundenakte.nr !== nr || vorbelegtFuer.current === nr) return;
    vorbelegtFuer.current = nr;
    const standard = orte.find((o) => o.standard) ?? (orte.length === 1 ? orte[0] : undefined);
    setzeOrt(standard?.name ?? "");
  }, [kunde, kundenakte, orte]);

  /* **Zwei Sorten Zeile, und nur eine hat einen Zeitraum** (18.09.2026).
     Leistung, Ware, Zuschlag und Freitext gehören zum Vorgang und nicht zum
     Kalender: Sie werden nicht disponiert, nicht auf Verfügbarkeit geprüft
     und nicht je Zeitfenster gefragt. Sie stehen deshalb unter den Mieten
     und nicht zwischen ihnen. */
  const mieten = useMemo(() => posten.filter(istMiete), [posten]);
  const weitere = useMemo(() => posten.filter((p) => !istMiete(p)), [posten]);

  /* **„Kunde holt ab" im Kopf** (Lisas Durchgang, 08.10.2026) — die Wahl für
     alle Posten mit Transport. Sie folgt den Posten (`abholungKopfNach`);
     nur im gemischten Fall bleibt stehen, was zuletzt für alle galt, und die
     abweichenden Zeilen sagen es. */
  const abholStand = abholungAllerStand(mieten);
  const [abholKopfRoh, setzeAbholKopf] = useState(() => abholungKopfVorgabe(mieten));
  const abholKopf = abholungKopfNach(abholStand, abholKopfRoh);
  useEffect(() => { setzeAbholKopf((alt) => abholungKopfNach(abholStand, alt)); }, [abholStand]);
  const fuerAlle = useAbholungFuerAlle();
  const abholKopfSetzen = (an: boolean) => { setzeAbholKopf(an); fuerAlle(mieten, an); };
  /* Wer „Ab Hof" wählt, meint: Der Kunde holt ab — als Vorgabe für alle.
     Nur auf Handgriff, nicht beim Vorbelegen (`abHofHoltAb`). */
  const ortWaehlen = (neu: string) => {
    if (abHofHoltAb(ort, neu) && abholStand !== null) abholKopfSetzen(true);
    setzeOrt(neu);
  };

  /** Die Posten nach Zeitfenster — zwei Fenster sind zwei Fragen. */
  const fenster = useMemo(() => {
    const karte = new Map<string, Merkposten[]>();
    mieten.forEach((p) => {
      const schluessel = fensterVon(p);
      const liste = karte.get(schluessel);
      if (liste) liste.push(p);
      else karte.set(schluessel, [p]);
    });
    return [...karte.entries()].map(([schluessel, liste]) => ({
      schluessel,
      von: liste[0]?.von ?? "",
      bis: liste[0]?.bis ?? "",
      posten: liste,
    }));
  }, [mieten]);

  /* ── Wann: der Zeitraum, der für alle gilt (08.10.2026) ─────────────
     **Dieselbe Quelle wie die Feldzeile von „Vermieten"**, kein zweiter
     Stand: Steht er an den Posten, ist es der der ersten Miete ohne eigenen
     Zeitraum; sonst der aus der Feldzeile. Wer ihn hier ändert, ändert ihn
     dort mit (`setzeWunsch`) — und an jedem Posten, der keinen eigenen hat.
     Ein Posten mit eigenem Zeitraum bleibt, wo er ist
     (`daten/eigenerzeitraum.ts`). */
  const { wunsch, setzeWunsch } = useVermietstand();
  const gemeinsam: Spanne = gemeinsamerMietzeitraum(posten, { von: wunsch.von, bis: wunsch.bis });
  /** Wie viele Mieten einen eigenen Zeitraum tragen — die Kopfzeile sagt es. */
  const eigeneZahl = mieten.filter((p) => weichtAb(p, gemeinsam)).length;
  /** Feiertage im gemeinsamen Zeitraum (0287) — am Telefon im Satz unter „Wann?". */
  const wannFeiertage = feiertagssatz(t, useFeiertageIm(gemeinsam.von, gemeinsam.bis), true);
  const alleSetzen = (neu: Spanne) => {
    if (!neu.von || !neu.bis) return;
    const ziel = { von: neu.von, bis: neu.bis };
    folgenDemGemeinsamen(posten, gemeinsam, ziel).forEach((x) => aendern(x.id, { von: x.von, bis: x.bis }));
    setzeWunsch({ ...wunsch, art: "spanne", von: ziel.von, bis: ziel.bis });
  };

  /* Ohne Antwort keine Summe (N12-01): „0,00" wäre eine Aussage. */
  const preiseUnterwegs = usePreiseUnterwegs();
  const summe = Math.round(
    posten.reduce((s, p) => s + (betraege[p.id] ?? postenSumme(p, schonGezaehlt.get(p))), 0) * 100,
  ) / 100;
  /**
   * **Hier wird der Kunde zur Pflicht** — und keinen Schritt früher.
   *
   * Luca, 17.09.2026: *„das sind optionale Felder, die spätestens
   * erforderlich sind, wenn ich auf ‚Jetzt vermieten' oder ‚Angebot
   * erstellen' klicke."* Oben in der Fläche fragt er nicht, er rechnet nur
   * mit (Regel 11); hier entsteht ein Vorgang, und ein Vorgang ohne
   * Empfänger ist keiner.
   *
   * Der Einsatzort bleibt freiwillig, auch hier: Es gibt Vermietungen ab Hof.
   */
  const kundeFehlt = !kunde;
  /* **Ohne Gegenstand entsteht kein Vorgang** — aber eine Leistung ist
     einer (Systemprüfung Befund 45, 25.09.2026). Bis dahin hieß die Regel
     „ohne Miete kein Vorgang": Der Kopf des Auftrags war eine Mietzeile, und
     eine Sammlung aus lauter Leistungen ging nicht hinaus. Lisa: *„Erschwert
     den Einstieg für reine Leistungsaufträge"* — die PV-Reinigung ist ein
     Auftrag ohne Gerät, und der Betrieb fährt sie. Jetzt reicht eine Zeile,
     die etwas berechnet: eine Miete, eine Leistung oder eine Ware. Eine
     Sammlung aus Freitext und Zuschlägen allein bleibt draußen — sie hätte
     nichts, woran ein Zuschlag hängt. Der Betreff kommt dann aus der ersten
     Zeile (`merklisteVermieten`). */
  const mieteFehlt = mieten.length === 0
    && !weitere.some((p) => p.art === "leistung" || p.art === "verkauf");
  const bereit = Boolean(kunde) && !mieteFehlt;
  /** Eine Vermietung braucht einen Kunden, keinen Interessenten. */
  const nurAngebot = kunde?.art === "interessent";
  /* **Im Angebotsweg kein Vermieten** (V-44, Durchgang 03.10.2026). „Angebot
     erstellen" öffnete dasselbe Blatt wie „Jetzt vermieten", mit dem Haken
     „telefonisch bestellt" und dem gelben Vermietknopf — wer den Haken
     setzte, vermietete, obwohl er ein Angebot wollte. Wer vermieten will,
     schließt das Blatt und nimmt „Jetzt vermieten" an der Merkliste. */
  const ohneVermieten = nurAngebot || art === "angebot";
  // Mit dem Haken ist der Satz „Erst den Haken setzen" erledigt.
  useEffect(() => {
    if (bestaetigt) setzeFehler((f) => (f === "merkliste.erstBestaetigen" ? "" : f));
  }, [bestaetigt]);
  /* **Eine Sperre ist eine Auskunft, kein Fehler nach dem Klick**
     (docs/38-vermietmodul.md, Punkt 6). Sie steht jetzt schon oben in der
     Fläche unter dem Kunden; hier steht sie noch einmal, weil hier der
     Vorgang entsteht. Sie hält nichts auf — was mit einem gesperrten Kunden
     geht und was nicht, entscheidet die Buchhaltung und nicht diese Maske. */
  const gesperrt = kunde?.offen === "stopp";

  /* **Ein Gerät, das nur mit Bediener hinausgeht, wird nicht ohne vermietet.**
     Luca, 17.09.2026: *„Ich kann ja nicht den Auftrag verplanen, ohne zu
     sagen: Das machen wir so. Ich muss es dann manuell nachholen."*

     Das Angebot bleibt offen: Es ist eine Option, und eine Option blockt
     nicht (Regel 6). Wer fährt, entscheidet die Disposition, wenn der Kunde
     zusagt — verbindlich wird es erst mit dem Auftrag. */
  const ohneBediener = posten.filter(bedienerFehlt);

  /* **Und dasselbe für das Pflichtzubehör** (18.09.2026). Ein Träger geht
     nicht ohne sein Pflichtanbauteil hinaus — die alte Schnellerfassung
     sperrte dafür ihren Knopf, die Fläche wusste bis dahin nichts davon
     (docs/38-vermietmodul.md, Punkt 2).

     **Der Server lässt es bewusst offen** (`gespannNachbuchen`): An einer
     bestehenden Position ließe es sich nicht nachwählen, und ein Riegel ohne
     Handgriff wäre eine Sackgasse. Hier gibt es den Handgriff — das Blatt am
     Posten, ein anderer Zeitraum, ein anderer Posten —, also steht der
     Riegel hier. Das **Angebot bleibt offen**: Eine Option blockt nicht
     (Regel 6 und 10), und bis zur Zusage kann ein Korb frei werden. */
  const ohnePflichtzubehoer = mieten.filter((p) => (pflichtJe[p.id] ?? []).length > 0);
  /* **Und was nur ein Angebot hält, ohne dass jemand „Trotzdem nehmen"
     gedrückt hat** (Befund 43). Bis dahin fiel das erst drüben auf — die
     Buchung wies ab, und die Meldung sagte nicht, an welcher Zeile. */
  const reserviertOffen = mieten.filter((p) => reserviertJe[p.id] === true);
  /* **Und was ohne Preis nicht vermietet wird** (0227, E1 — die
     Anlieferung): ein Zuschlag mit „Preis immer individuell", für den noch
     niemand einen Satz genannt hat. Der Server hält die Annahme ebenso an
     (`annahme.preis_fehlt`); hier fällt es auf, bevor ein halber Vorgang
     entsteht. Das Angebot bleibt offen. */
  const ohnePreis = mieten.filter((p) => fehlendePreise(p).length > 0);
  /* **Und eine Leistung, deren Grad fehlt** (P8-05). Mehrere Preisstufen,
     keine Vorgabe, keine gewählt: Die Annahme friert dann keinen Satz ein,
     und AB und Rechnung sagten 0,00, während hier ein Preis stand. Der Server
     hält die Annahme ebenso an (`annahme.grad_fehlt`); ein Angebot geht. */
  const { data: leistungsarten } = useLeistungsarten();
  const ohneGrad = weitere.filter((p) => {
    if (p.art !== "leistung" || !p.leistungsartId) return false;
    const l = (leistungsarten?.zeilen ?? []).find((x) => x.id === p.leistungsartId);
    return !!l && leistungssatz(l, p.kategorieId ?? null, p.menge).wahlNoetig;
  });

  /* ── Die Stelle, auf die die Meldung zeigt (Befund 43) ────────────────
     Je Sorte die Posten, an denen es klemmt — und zwar **jetzt**: Wer den
     Bediener wählt oder „Trotzdem nehmen" drückt, sieht den Rahmen an
     dieser Zeile verschwinden. Klemmt es nirgends mehr, geht auch die
     Meldung weg; sie spräche sonst von etwas, das nicht mehr da ist. */
  const hervorJe = useMemo(() => {
    const liste = stelle === "bediener" ? ohneBediener
      : stelle === "pflicht" ? ohnePflichtzubehoer
        : stelle === "reserviert" ? reserviertOffen
          : stelle === "preis" ? ohnePreis : [];
    return new Set(liste.map((p) => p.id));
  }, [stelle, ohneBediener, ohnePflichtzubehoer, reserviertOffen, ohnePreis]);
  const hervor = useCallback(
    (id: string): Fehlerstelle | null => (hervorJe.has(id) ? stelle : null),
    [hervorJe, stelle],
  );
  useEffect(() => {
    if (stelle && hervorJe.size === 0) { setzeStelle(null); setzeFehler(""); }
  }, [stelle, hervorJe]);
  /* Die erste rot umrandete Stelle kommt ins Bild — die Meldung steht unten
     bei den Knöpfen, die Zeile womöglich eine Bildschirmhöhe weiter oben. */
  useEffect(() => {
    if (!stelle) return;
    const erste = document.querySelector(".ma-zeilen .fehlerhaft, .ma-reserviert.fehlerhaft");
    erste?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [stelle]);

  /** Eine Meldung, die auf eine Stelle zeigt. */
  const meldenAn = (schluessel: string, wo: Fehlerstelle) => {
    setzeFehler(schluessel);
    setzeFehlerDaten({});
    setzeStelle(wo);
  };

  async function abschliessen(welche: Abschlussart, jetztNachtraeglich = false) {
    if (!kunde || mieteFehlt) return;
    const nachtraeglich = nachtraeglichSchicken(jetztNachtraeglich, nachtraeglichJa);
    if (jetztNachtraeglich) setzeNachtraeglichJa(true);
    setzeFehlerRoh(null);
    /* **Der gesperrte Knopf sagt, was fehlt** (V-14, 03.10.2026). Mit
       `disabled` sprang Tab über „Jetzt vermieten" auf „Angebot erstellen",
       und Enter legte ein Angebot an, das niemand wollte (Regel 10). Der
       Knopf bleibt deshalb erreichbar (`aria-disabled`) und nennt hier den
       Haken, statt still zu bleiben. */
    if (welche === "vermieten" && !bestaetigt) {
      setzeFehler("merkliste.erstBestaetigen");
      setzeFehlerDaten({});
      return;
    }
    if (welche === "vermieten" && ohneBediener.length) {
      meldenAn("merkliste.bedienerFehlt", "bediener");
      return;
    }
    if (welche === "vermieten" && ohnePflichtzubehoer.length) {
      meldenAn("merkliste.pflichtzubehoerFehlt", "pflicht");
      return;
    }
    if (welche === "vermieten" && reserviertOffen.length) {
      meldenAn("merkliste.reserviertOffen", "reserviert");
      return;
    }
    if (welche === "vermieten" && ohneGrad.length) {
      setzeFehler("merkliste.gradFehlt");
      setzeFehlerDaten({ was: ohneGrad.map((p) => p.bezeichnung).join(", ") });
      setzeStelle(null);
      return;
    }
    if (welche === "vermieten" && ohnePreis.length) {
      meldenAn("merkliste.preisFehlt", "preis");
      setzeFehlerDaten({
        was: [...new Set(ohnePreis.flatMap((p) => fehlendePreise(p).map((z) => z.bezeichnung)))].join(", "),
      });
      return;
    }
    setzeFehler("");
    setzeStelle(null);
    const eingabe = {
      kunde: kunde.nr,
      ...(ort.trim() ? { ort: ort.trim() } : {}),
      ...(vortext.trim() ? { vortext: vortext.trim() } : {}),
      ...(nachtext.trim() ? { nachtext: nachtext.trim() } : {}),
      ...(hofhinweis.trim() ? { hofhinweis: hofhinweis.trim() } : {}),
      ...(notiz.trim() ? { notiz_intern: notiz.trim() } : {}),
      ...(bestellnummer.trim() ? { bestellnummer: bestellnummer.trim() } : {}),
      // „Nachträglich erfassen" bestätigt — der Mietbeginn liegt zurück (F20).
      ...(nachtraeglich ? { nachtraeglich: true } : {}),
      /* **Was keine Miete ist, geht als eigene Position mit** (18.09.2026).
         Derselbe Weg, den die Schnellerfassung ging (`Schnellposition`): Die
         Kennung aus dem Katalog schlägt Bezeichnung, Einheit und Satz drüben
         nach; ohne sie trägt die Zeile alles selbst. Eine Textzeile hat
         keinen Betrag — das ist ihr ganzer Zweck. */
      positionen: weitere.map((p) => ({
        art: p.art as "leistung" | "verkauf" | "zuschlag" | "text",
        bezeichnung: p.bezeichnung || t("merkliste.ohneNamen"),
        menge: p.art === "text" ? 0 : p.menge,
        einzelpreis: p.art === "text" ? 0 : (p.einzelpreis ?? p.netto),
        ...(p.leistungsartId ? { leistungsart_id: p.leistungsartId } : {}),
        ...(p.kategorieId && p.art === "leistung" ? { kategorie_id: p.kategorieId } : {}),
        ...(p.aufmass ? { aufmass: true } : {}),
        ...(p.wareId ? { ware_id: p.wareId } : {}),
        ...(p.zuschlagCode ? { zuschlag_code: p.zuschlagCode } : {}),
        // Der Termin einer Leistung (P10-02) — sonst „Termin offen".
        ...(p.art === "leistung" && p.von ? { von: p.von, bis: p.bis || p.von } : {}),
      })),
      zeilen: mieten.map((p) => ({
        typ_id: p.typId,
        ...(p.varianteId ? { variante_id: p.varianteId } : {}),
        // Ohne Bezeichnung weist die API die Position ab; ein Wort ist besser
        // als ein Abbruch, und es kommt nur vor, wenn der Stamm den Typ nicht
        // kennt — dann stimmt ohnehin etwas anderes nicht.
        bezeichnung: namen(p) || t("merkliste.ohneNamen"),
        von: p.von,
        bis: p.bis,
        bedient: p.bedient,
        menge: p.menge,
        /* Was das Blatt am Posten dazugelegt hat (docs/38-vermietmodul.md,
           Schritt 2). Alles freiwillig: Ein Posten aus der Preisauskunft
           trägt nichts davon, und der Weg kommt damit aus. */
        ...(p.maschine ? { maschine: p.maschine } : {}),
        /* Wer ein reserviertes Gerät genommen hat, hat das im Blatt bestätigt
           (18.09.2026). Ohne die Bestätigung weist die API ab. */
        ...(p.reservierungUebergehen ? { reservierung_uebergehen: true } : {}),
        ...(p.bediener ? { bediener: p.bediener } : {}),
        ...(p.zubehoer?.length ? { zubehoer: p.zubehoer } : {}),
        ...(p.einzelpreis === undefined ? {} : { einzelpreis: p.einzelpreis }),
        /* Nur, was von Hand daran steht: Ein Zuschlag, den niemand angefasst
           hat, wird drüben aus der Regel gerechnet — ihn als Zeile
           mitzuschicken hieße, denselben Betrag zweimal zu führen. */
        zuschlaege: p.zuschlaege
          .filter((z) => z.aus || z.einzelpreis !== undefined)
          .map((z) => ({
            code: z.code,
            bezeichnung: z.bezeichnung,
            menge: z.menge ?? 1,
            einzelpreis: z.aus ? 0 : (z.einzelpreis ?? 0),
            ...(z.jeAuftrag ? { je_auftrag: true } : {}),
          })),
      })),
    };
    try {
      const ergebnis = welche === "vermieten"
        ? await vermieten.mutateAsync(eingabe)
        : await angebot.mutateAsync(eingabe);
      const { nr } = ergebnis;
      /* **Wem das Gerät weggenommen wurde, findet es an seinem Vorgang**
         (18.09.2026). Wer ein reserviertes Stück nimmt, hat das im Blatt
         bestätigt; das Angebot daneben steht danach ohne Gerät da. Ohne diese
         Zeile bliebe dort nur „Kein Gerät eingeplant" — ohne Grund und ohne
         Namen. Sie wird hier geschrieben und nicht im Server: Ein Satz ist
         sichtbarer Text und gehört in die Sprachdatei (CLAUDE.md). */
      for (const weg of ergebnis.aufgehoben) {
        await notizAnlegen(
          weg.vorgang,
          t("merkliste.reservierungAufgehobenNotiz", {
            geraet: weg.kennung, auftrag: nr, von: tagKurz(weg.von), bis: tagKurz(weg.bis),
          }),
        ).catch(() => undefined);
      }
      abgeschicktEntfernen(posten.map((p) => p.id));
      /* Und die Frage oben in der Vermietfläche — der nächste Anruf fängt
         nicht mit dem Zeitraum dieses Auftrags an (Systemprüfung A-06). */
      frageNachAbschlussLeeren();
      fertig();
      ruf(welche === "vermieten" ? "vermietet" : "angebotEntwurf", { nr });
      void gehZu(welche === "vermieten"
        ? { to: "/auftraege/$nr", params: { nr } }
        : { to: "/angebote/$nr", params: { nr } });
    } catch (f) {
      const eigen = f instanceof Error
        && (f.message.startsWith("schnell.") || f.message.startsWith("merkliste."));
      const schluessel = eigen && f instanceof Error ? f.message : fehlerPfad(f);
      setzeFehler(schluessel);
      setzeFehlerDaten(fehlerWerte(f));
      setzeFehlerRoh(f);
      /* Weist drüben die Buchung ab, weil ein Angebot das Stück hält, zeigt
         die Meldung auf die Zeile mit „Trotzdem nehmen" (Befund 43). */
      setzeStelle(reserviertOffen.length
        && (schluessel.includes("reserviert") || schluessel.endsWith("keineFreieMaschine"))
        ? "reserviert" : null);
    }
  }

  /* ══════════════ Am Telefon ══════════════
     Ein Blatt von unten, vier Abschnitte in Handyformen — die Begründung
     steht oben im Kopf der Datei. Was hier anders ist als am Schreibtisch,
     ist die Zeichnung, nicht die Rechnung: `abschliessen`, die Riegel und
     die frisch geholten Beträge sind dieselben. */
  if (telefon) {
    const trenn = ` ${t("allgemein.trennpunkt")} `;
    const gewaehlterOrt = orte.find((o) => o.name === ort) ?? null;
    const heute = heuteTag();
    const knoepfe = (
      <>
        {fehler ? <p className="ma-fehler" role="alert"><T k={fehler} werte={fehlerDaten} /></p> : null}
        <Nachtraeglichfrage fehler={fehlerRoh} laeuft={laeuft} ja={() => void abschliessen("vermieten", true)} />
        {ohneVermieten ? null : (
          <button
            type="button"
            className={`handy-knopf${art === "vermieten" ? "" : " leise"}`}
            disabled={!bereit || laeuft}
            aria-disabled={!bestaetigt || undefined}
            onClick={() => void abschliessen("vermieten")}
          >
            {t("merkliste.vermieten")}
          </button>
        )}
        <button
          type="button"
          className={`handy-knopf${art === "angebot" || nurAngebot ? "" : " leise"}`}
          disabled={!bereit || laeuft}
          onClick={() => void abschliessen("angebot")}
        >
          {t("merkliste.angebot")}
        </button>
      </>
    );
    /* Die beiden Belegtexte tragen ihre Bausteinart und ihren Beleg (0105). */
    const TEXTE: Record<Textart, { schild: string; platz: string; bausteine: { art: Bausteinart; beleg: Belegart } }> = {
      vortext: { schild: "textbausteine.vortext", platz: "textbausteine.vortextPlatz", bausteine: { art: "vortext", beleg: belegart } },
      nachtext: { schild: "textbausteine.nachtext", platz: "textbausteine.nachtextPlatz", bausteine: { art: "nachtext", beleg: belegart } },
    };
    const offenerText = textOffen ? TEXTE[textOffen] : null;

    return (
      <>
        <Blattvonunten
          offen={offen}
          schliessen={schliessen}
          rast="gross"
          titel={t(art === "vermieten" ? "merkliste.abschlussVermieten" : "merkliste.abschlussAngebot")}
          unter={t("merkliste.abschlussUnter")}
          fuss={knoepfe}
        >
          <div className="mat">
            {/* ── Für wen, wohin, wann — und die Bestellnummer (08.10.2026) ──
                Luca: *„Bei der Schnellvermietung müssen ‚für wen', ‚wohin',
                ‚wann' und die Bestellnummer direkt sichtbar sein."* Dieselben
                vier Angaben wie am Schreibtisch, einspaltig: drei Zeilen, die
                ihr Blatt öffnen, und die Bestellnummer als Feld (44 px, 16 px).
                Der Kunde ist Pflicht und wird hier zur Pflicht — keinen Schritt
                früher (Regel 11). Der Einsatzort bleibt freiwillig: Es gibt
                Vermietungen ab Hof. */}
            <Abschnitt titel={t("merkliste.schrittKopf")}>
              <Zeilenblatt>
                <Kundenwahl
                  id="ma-kunde"
                  wert={kunde}
                  aendern={(k) => { setzeKunde(k); vorbelegtFuer.current = ""; }}
                  art={art === "angebot" ? "interessent" : "kunde"}
                  anker={(oeffnen) => (
                    <Zweizeiler
                      beiKlick={oeffnen}
                      punkt={kundeFehlt ? "acht" : gesperrt ? "stopp" : "ruhig"}
                      titel={t("merkliste.schrittKunde")}
                      unter={kunde
                        ? [kunde.name, kunde.ort, kunde.nr].filter(Boolean).join(trenn)
                        : kundeWeg
                          ? t("merkliste.kundeNichtMehrWaehlbar", { name: wer.name || wer.nr })
                          : t(art === "vermieten" ? "merkliste.kundePflichtVermieten" : "merkliste.kundePflichtAngebot")}
                      satz={kundeFehlt}
                    />
                  )}
                />
                <Zweizeiler
                  {...(kunde ? { beiKlick: () => setzeOrteOffen(true) } : {})}
                  punkt={gewaehlterOrt && !anschriftVon(gewaehlterOrt) ? "acht" : "ruhig"}
                  titel={t("merkliste.schrittOrt")}
                  unter={kunde
                    ? [ort || t("vermieten.ortOhne"), gewaehlterOrt ? ortsatzVon(t, gewaehlterOrt) : ""].filter(Boolean).join(trenn)
                    : t("merkliste.erstKunde")}
                  satz={!kunde}
                />
                {/* „Kunde holt ab" direkt unter „Wohin?" — für alle Posten mit Transport (08.10.2026). */}
                <AbholungKopf posten={mieten} kopf={abholKopf} setzen={abholKopfSetzen} telefon />
                <Zweizeiler
                  beiKlick={() => setzeZeitOffen(true)}
                  titel={t("merkliste.schrittWann")}
                  unter={[
                    gemeinsam.von && gemeinsam.bis
                      ? zeitraumText(t, gemeinsam.von, tageZwischen(gemeinsam.von, gemeinsam.bis))
                      : t("merkliste.wannOffen"),
                    eigeneZahl ? t(eigeneZahl === 1 ? "merkliste.eigeneZeitraeumeEins" : "merkliste.eigeneZeitraeume", { zahl: eigeneZahl }) : "",
                    wannFeiertage?.text ?? "",
                  ].filter(Boolean).join(trenn)}
                />
              </Zeilenblatt>
              <div className="handy-feld mat-feld">
                <label htmlFor="mat-bestellnummer">{t("bestellnummer.schild")}</label>
                <input
                  id="mat-bestellnummer"
                  maxLength={80}
                  autoCapitalize="off"
                  placeholder={t("bestellnummer.platz")}
                  value={bestellnummer}
                  onChange={(e) => setzeBestellnummer(e.target.value)}
                />
              </div>
              {nurAngebot ? <p className="mat-satz">{t("merkliste.interessent")}</p> : null}
              {gesperrt ? (
                <p className="mat-satz stopp">
                  <Punkt art="stopp" />
                  <span><T k="schnell.kundeGesperrt" /></span>
                </p>
              ) : null}
            </Abschnitt>

            {/* ── Die Posten — und ist es frei? ──
                Je Posten eine Zeile mit der Ampel der Belegung und dem frisch
                geholten Betrag; ein eigener Zeitraum steht in der Zeile. Die
                Zeile öffnet das Blatt zum Posten. */}
            <Abschnitt
              titel={t("merkliste.schrittPosten")}
              auskunft={posten.length === 1 ? t("merkliste.einPosten") : t("merkliste.posten", { zahl: posten.length })}
            >
              {mieteFehlt ? <Leersatz>{t("merkliste.mieteFehlt")}</Leersatz> : null}
              {posten.length ? (
                <Zeilenblatt>
                  {fenster.map((f) => (
                    <Fenster
                      key={f.schluessel}
                      von={f.von}
                      bis={f.bis}
                      posten={f.posten}
                      name={namen}
                      kundeNr={kunde?.nr ?? ""}
                      offen={offen}
                      entfernen={entfernen}
                      aendern={aendern}
                      melden={melden}
                      pflichtMelden={pflichtMelden}
                      reserviertMelden={reserviertMelden}
                      hervor={hervor}
                      nicht={nichtAn}
                      gemeinsam={gemeinsam}
                      abholKopf={abholKopf}
                      telefon
                      oeffnen={(p) => setzeBlattPosten(p.id)}
                    />
                  ))}
                  {weitere.map((p) => <Weiterezeile key={p.id} p={p} entfernen={entfernen} aendern={aendern} telefon />)}
                </Zeilenblatt>
              ) : null}
            </Abschnitt>

            {/* ── Was noch? ──
                Der Hinweis für den Hof und die interne Notiz stehen offen da
                (Luca, 08.10.2026: *„Der muss direkt sichtbar sein."*); Vor- und
                Nachtext der Auftragsbestätigung sind Zeilen, die sagen, was
                dasteht oder was der Standard auf den Beleg bringt — getippt
                wird im Blatt dahinter. */}
            <Abschnitt titel={t("merkliste.schrittTexte")} auskunft={t("merkliste.freiwillig")}>
              <div className="handy-feld mat-feld">
                <label htmlFor="mat-hofhinweis">{t("merkliste.hofhinweis")}</label>
                <textarea
                  id="mat-hofhinweis"
                  rows={3}
                  aria-invalid={textZuLang(hofhinweis)}
                  placeholder={t("merkliste.hofhinweisPlatz")}
                  value={hofhinweis}
                  onChange={(e) => setzeHofhinweis(e.target.value)}
                />
                <Laengensatz text={hofhinweis} />
                <p className="mat-satz">{t("merkliste.hofhinweisWas")}</p>
              </div>
              <div className="handy-feld mat-feld">
                <label htmlFor="mat-notiz">{t("merkliste.notizIntern")}</label>
                <textarea
                  id="mat-notiz"
                  rows={2}
                  aria-invalid={textZuLang(notiz)}
                  placeholder={t("merkliste.notizInternPlatz")}
                  value={notiz}
                  onChange={(e) => setzeNotiz(e.target.value)}
                />
                <Laengensatz text={notiz} />
              </div>
              <Zeilenblatt>
                {(["vortext", "nachtext"] as const).map((welcher) => {
                  const w = TEXTE[welcher];
                  return (
                    <Textzeile
                      key={welcher}
                      schild={w.schild}
                      art={w.bausteine.art}
                      beleg={w.bausteine.beleg}
                      wert={textVon(welcher)}
                      oeffnen={() => textOeffnen(welcher)}
                    />
                  );
                })}
              </Zeilenblatt>
            </Abschnitt>

            {/* ── Und dann? ──
                Die Summe, und die letzte Bestätigung vor dem verbindlichen
                Auftrag (Regel 10): *„da muss man nochmal bestätigen, der Kunde
                hat telefonisch bestellt."* Eine Zeile, die man antippt — der
                Haken rechts und `aria-pressed` tragen den Zustand. Nur am
                Vermieten; ein Angebot bindet niemanden. */}
            <Abschnitt titel={t("merkliste.schrittAbschluss")}>
              <Zeilenblatt>
                <Wertzeile was={t("merkliste.summe")} summe>{preiseUnterwegs ? t("allgemein.zahlOffen") : euro(summe)}</Wertzeile>
                {ohneVermieten ? null : (
                  <Zweizeiler
                    beiKlick={() => setzeBestaetigt((x) => !x)}
                    gedrueckt={bestaetigt}
                    punkt={bestaetigt ? "gut" : "acht"}
                    winkel={false}
                    titel={t("merkliste.bestaetigen")}
                    unter={t(bestaetigt ? "merkliste.bestaetigt" : "merkliste.bestaetigenSatz")}
                    satz
                    {...(bestaetigt ? { rechts: <Zeichen name="haken" /> } : {})}
                  />
                )}
              </Zeilenblatt>
              <p className="mat-satz">
                {t(art === "vermieten" ? "merkliste.fussVermieten" : "merkliste.fussAngebot")}
              </p>
            </Abschnitt>
          </div>
        </Blattvonunten>

        {/* Die Blätter darüber — jedes dasselbe wie überall sonst. */}
        <Ortblatt
          offen={offen && orteOffen}
          orte={orte}
          gewaehlt={ort}
          kunde={kunde?.name}
          schliessen={() => setzeOrteOffen(false)}
          waehlen={(name) => { ortWaehlen(name); setzeOrteOffen(false); }}
          anlegen={() => { setzeOrteOffen(false); setzeOrtBlatt(""); }}
        />
        <OrtAnlegen
          offen={ortBlatt !== null}
          kunde={kunde}
          name={ortBlatt ?? ""}
          erster={orte.length === 0}
          schliessen={() => setzeOrtBlatt(null)}
          angelegt={(neu) => { setzeOrtBlatt(null); setzeOrt(neu.name); }}
        />
        {offen && zeitOffen ? (
          <Zeitraumblatt
            von={gemeinsam.von ?? heute}
            bis={gemeinsam.bis ?? gemeinsam.von ?? heute}
            titel={t("merkliste.schrittWann")}
            schliessen={() => setzeZeitOffen(false)}
            uebernehmen={(von, bis) => { alleSetzen({ von, bis }); setzeZeitOffen(false); }}
          />
        ) : null}
        <Postenblatt
          offen={offen && blattPosten !== null}
          posten={posten.find((p) => p.id === blattPosten) ?? null}
          kunde={kunde?.nr ?? ""}
          schliessen={() => setzeBlattPosten(null)}
        />
        {/* Der eine Text: das Feld als Blatt, die Bausteine darüber (am
            Telefon tippt diese Sätze niemand). Übernommen wird mit dem Knopf;
            wer mit halbem Satz zurück will, wird gefragt (`schmutzig`). Die
            interne Notiz hat keine Bausteine — sie geht auf keinen Beleg. */}
        <Blattvonunten
          offen={offen && offenerText !== null}
          schliessen={() => setzeTextOffen(null)}
          rast="gross"
          titel={offenerText ? t(offenerText.schild) : ""}
          {...(kunde ? { unter: kunde.name } : {})}
          schmutzig={textOffen !== null && feld.trim() !== textVon(textOffen).trim()}
          fuss={(
            <button
              type="button"
              className="handy-knopf"
              disabled={textOffen === null || feld.trim() === textVon(textOffen).trim()}
              onClick={textUebernehmen}
            >
              {t("merkliste.textUebernehmen")}
            </button>
          )}
        >
          {offenerText ? (
            <div className="mat-text">
              <Bausteinzugriff
                art={offenerText.bausteine.art}
                beleg={offenerText.bausteine.beleg}
                wert={feld}
                einsetzen={setzeFeld}
              />
              <div className="handy-feld">
                <textarea
                  rows={5}
                  // Ein Belegtext wird gesagt, nicht gekürzt (N4-16).
                  value={feld}
                  autoCapitalize="sentences"
                  aria-label={t(offenerText.schild)}
                  placeholder={t(offenerText.platz)}
                  onChange={(e) => setzeFeld(e.target.value)}
                />
                <Laengensatz text={feld} />
              </div>
              <p className="mat-satz">{t("angebotTexte.standardSatz")}</p>
            </div>
          ) : null}
        </Blattvonunten>
      </>
    );
  }

  const gewaehlterOrtTisch = orte.find((o) => o.name === ort) ?? null;
  return (
    /* **Ein Fenster, und ein kleineres als die Fläche darunter.** Luca,
       17.09.2026: *„Das Fenster, was dann aufgeht, muss halt entsprechend
       modal sein, was ein bisschen kleiner ist."* Es füllte vorher die ganze
       Maske und sah damit aus wie eine weitere Maske statt wie der letzte
       Schritt. 860 px, nicht das Kompaktmaß von 720: Die Postenzeilen darin
       tragen einen Zeitraumwähler und einen Betrag nebeneinander, und bei 720
       bricht das um. */
    <Deckblatt
      offen={offen}
      kompakt
      weite={860}
      schliessen={schliessen}
      titel={t(art === "vermieten" ? "merkliste.abschlussVermieten" : "merkliste.abschlussAngebot")}
      untertitel={t("merkliste.abschlussUnter")}
    >
      <div className="ma-blatt">
        {/* ── Der Kopf: für wen · wohin · wann · Bestellnr. (08.10.2026) ──
            Luca: *„Bei der Schnellvermietung müssen ‚für wen', ‚wohin',
            ‚wann' und die Bestellnummer direkt sichtbar sein."* Ein Formular
            im Raster, Felder 40 px, nichts eingeklappt. **Die Werte sind die
            der Feldzeile von „Vermieten"** — Kunde und Ort stehen am Entwurf
            (`useMerkliste().wer`), der Zeitraum ist die Frage der Fläche
            (`useVermietstand`). Wer sie hier ändert, ändert sie dort.

            Der Kunde wird hier zur Pflicht — keinen Schritt früher (Regel 11);
            wer fehlt, wird in der Auswahl angelegt (Luca: *„Wenn man den
            Kunden noch nicht hat, legt man ihn in diesem Flow an."*). Der
            Einsatzort bleibt freiwillig: Es gibt Vermietungen ab Hof. */}
        <section className={`ma-teil${kundeFehlt ? " fehlt" : ""}`}>
          <div className="ma-kopfform">
            <div className="ma-feld">
              <label className="ma-schild" htmlFor="ma-kunde">
                {t("merkliste.schrittKunde")}
                {kundeFehlt ? <span className="ma-pflicht">{t("allgemein.pflicht")}</span> : null}
              </label>
              <Kundenwahl
                id="ma-kunde"
                wert={kunde}
                aendern={(k) => { setzeKunde(k); vorbelegtFuer.current = ""; }}
                art={art === "angebot" ? "interessent" : "kunde"}
              />
            </div>
            {/* „Wohin?" ist dasselbe Feld wie in „Vermieten": „Ab Hof", die
                Orte des Kunden, und als letzte Wahl „Einsatzort anlegen" —
                das Blatt dafür geht über dem Abschluss auf. */}
            <div className="ma-feld">
              {/* „Kunde holt ab" neben „Wohin?" — für alle Posten mit Transport (08.10.2026). */}
              <div className="ma-schild">
                <label htmlFor="ma-ort">{t("merkliste.schrittOrt")}</label>
                <AbholungKopf posten={mieten} kopf={abholKopf} setzen={abholKopfSetzen} />
              </div>
              <select
                id="ma-ort"
                className={`feld${gewaehlterOrtTisch && !anschriftVon(gewaehlterOrtTisch) ? " ma-ort-fehlt" : ""}`}
                value={kunde ? ort : ""}
                title={gewaehlterOrtTisch ? ortsatzVon(t, gewaehlterOrtTisch) || undefined : undefined}
                onChange={(e) => {
                  if (e.target.value === "+") { setzeOrtBlatt(""); return; }
                  ortWaehlen(e.target.value);
                }}
              >
                <option value="">{t("vermieten.ortOhne")}</option>
                {orte.map((o) => (
                  <option key={o.uuid ?? o.name} value={o.name}>{o.name}</option>
                ))}
                {kunde
                  ? <option value="+">{t("merkliste.ortAnlegen")}</option>
                  : <option value="-" disabled>{t("merkliste.erstKunde")}</option>}
              </select>
            </div>
            <div className="ma-feld">
              <label className="ma-schild" htmlFor="ma-zeit-alle">{t("merkliste.schrittWann")}</label>
              <Zeitraum
                id="ma-zeit-alle"
                wert={gemeinsam}
                schild={t("merkliste.schrittWann")}
                ohneTage
                ganz
                aendern={alleSetzen}
              />
              <Feiertagshinweis von={gemeinsam.von} bis={gemeinsam.bis} miettag klasse="ma-feiertag" />
              <Fruehabholungshinweis von={gemeinsam.von} klasse="ma-feiertag" />
            </div>
            <div className="ma-feld">
              <label className="ma-schild" htmlFor="ma-bestellnummer" title={t("bestellnummer.was")}>
                {t("bestellnummer.schild")}
              </label>
              <input
                className="feld mono"
                id="ma-bestellnummer"
                maxLength={80}
                placeholder={t("bestellnummer.platz")}
                value={bestellnummer}
                onChange={(e) => setzeBestellnummer(e.target.value)}
              />
            </div>
          </div>

          {/* **Was fehlt, steht da, wo es fehlt** — nicht als Fehlerband
              irgendwo. Wer den Knopf drückt und nichts passiert, sucht; wer
              die Zeile hier liest, weiß es. */}
          {kundeFehlt ? (
            <p className="ma-hinweis acht">
              {kundeWeg
                ? `${t("merkliste.kundeNichtMehrWaehlbar", { name: wer.name || wer.nr })} ${t("fehler.kundeDeaktiviert")}`
                : t(art === "vermieten" ? "merkliste.kundePflichtVermieten" : "merkliste.kundePflichtAngebot")}
            </p>
          ) : null}
          {nurAngebot ? (
            <p className="ma-hinweis">{t("merkliste.interessent")}</p>
          ) : null}
          {gesperrt ? (
            <p className="ma-hinweis stopp">
              <Punkt art="stopp" />
              <span><T k="schnell.kundeGesperrt" /></span>
            </p>
          ) : null}
          {eigeneZahl ? (
            <p className="ma-hinweis">
              {t(eigeneZahl === 1 ? "merkliste.eigeneZeitraeumeEins" : "merkliste.eigeneZeitraeume", { zahl: eigeneZahl })}
            </p>
          ) : null}
          {/* **Was für jemand anderen gemerkt ist, geht nicht mit** (Lisa,
              24.09.2026) — es steht hier, damit niemand es sucht, und geht
              nur auf Handgriff über. Übernommen wird es neu gerechnet. */}
          {fremde.length ? (
            <div className="ma-fremde">
              <p className="ma-hinweis acht">
                {/* Einzahl und Mehrzahl (N4-14): „1 Posten sind …" */}
                {t(fremde.length === 1 ? "merkliste.fremdeHinweisEins" : "merkliste.fremdeHinweis", { zahl: fremde.length })}
              </p>
              <ul>
                {fremde.map((p) => (
                  <li key={p.id}>
                    <span>
                      <b>{namen(p) || t("merkliste.ohneNamen")}</b>
                      {" · "}
                      {t("merkliste.nochFuer", { kunde: p.kundeName || p.kunde || "" })}
                    </span>
                    {wer.nr ? (
                      <button
                        type="button"
                        className="knopf klein"
                        onClick={() => aendern(p.id, {
                          kunde: wer.nr, kundeName: wer.name,
                          ...(istMiete(p) && p.gerechnet ? { zeitraumNeu: true } : {}),
                        })}
                      >
                        {t("merkliste.fuerKundeUebernehmen", { kunde: wer.name || wer.nr })}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        {/* ── Die Posten — und ist es frei? ──────────────────────────
            Luca: *„Zwischenschritt ist, dass man den Zeitraum final klärt
            und prüft, ob es verfügbar ist und wann."* Lage und Zeitraum
            stehen an derselben Zeile; jede Zeile klappt auf (Einstellungen,
            Aufstellung, Transport). Ein Posten darf einen eigenen Zeitraum
            haben — dann steht es an ihm, mit „wie alle" daneben. */}
        <section className="ma-teil">
          <h4 className="ma-gruppe">
            {t("merkliste.schrittPosten")}
            <span className="ma-gruppe-zahl">
              {posten.length === 1 ? t("merkliste.einPosten") : t("merkliste.posten", { zahl: posten.length })}
            </span>
          </h4>

          {fenster.map((f) => (
            <Fenster
              key={f.schluessel}
              von={f.von}
              bis={f.bis}
              posten={f.posten}
              name={namen}
              kundeNr={kunde?.nr ?? ""}
              offen={offen}
              entfernen={entfernen}
              aendern={aendern}
              melden={melden}
              pflichtMelden={pflichtMelden}
              reserviertMelden={reserviertMelden}
              hervor={hervor}
              nicht={nichtAn}
              oeffnen={(p) => setzeBlattPosten(p.id)}
              aufe={aufe}
              setzeAuf={setzeAuf}
              gemeinsam={gemeinsam}
              abholKopf={abholKopf}
            />
          ))}

          {/* **Was keinen Zeitraum hat, steht darunter** (18.09.2026) — mit
              einer Zeile davor, damit niemand sie unter den Mieten sucht.
              Sie gehen als eigene Positionen an denselben Vorgang, in
              derselben Reihenfolge wie hier. */}
          {weitere.length ? (
            <>
              <span className="ma-gruppe ma-weitere-schild">
                {t("merkliste.weitereZeilen")}
              </span>
              <ul className="ma-zeilen">
                {weitere.map((p) => (
                  <Weiterezeile key={p.id} p={p} entfernen={entfernen} aendern={aendern} />
                ))}
              </ul>
            </>
          ) : null}

          {mieteFehlt ? (
            <p className="ma-hinweis acht">{t("merkliste.mieteFehlt")}</p>
          ) : null}
        </section>

        {/* ── Was noch? ──────────────────────────────────────────────
            **Der Hinweis für den Hof ist der Vortext des Lieferscheins** —
            das Papier, das mit der Maschine hinausgeht (Übergabeprotokoll).
            Er und die interne Notiz stehen offen da (Luca, 08.10.2026: *„Man
            muss einen Hinweis für den Hof haben. Der muss direkt sichtbar
            sein."*). Zugeklappt sind nur Vor- und Nachtext des Belegs, und
            die Zeile sagt, ob der Standard gilt (*„Die Texte kann man
            aufklappen"*).

            **Die Texte hängen an der Belegart** (Migration 0105). Beim
            Vermieten sind es die der Auftragsbestätigung — die
            Direktvermietung hat kein Angebot (Regel 10). Beim Angebot sind
            es die des Angebots. */}
        <section className={`ma-teil${posten.length ? "" : " still"}`}>
          <h4 className="ma-gruppe">
            {t("merkliste.schrittTexte")}
            <span className="ma-gruppe-zahl">{t("merkliste.freiwillig")}</span>
          </h4>
          {/* **Der Hinweis für den Hof ist kein Belegtext** (0284): ein
              Feld ohne Bausteine des Lieferschein-Vortexts und ohne dessen
              Standard als Platzhalter. Er wird beim Anlegen der Hinweis am
              Vorgang — derselbe wie in den Kopfdaten von Angebot und Auftrag. */}
          <div className="ma-text">
            <div className="ma-text-kopf">
              <label className="feld-schild" htmlFor="ma-hofhinweis">
                {t("merkliste.hofhinweis")}
              </label>
              <span className="ma-wohin">{t("merkliste.hofhinweisWas")}</span>
            </div>
            <textarea
              className="feld texte-feld"
              id="ma-hofhinweis"
              rows={3}
              aria-invalid={textZuLang(hofhinweis)}
              placeholder={t("merkliste.hofhinweisPlatz")}
              value={hofhinweis}
              onChange={(e) => setzeHofhinweis(e.target.value)}
            />
            <Laengensatz text={hofhinweis} />
          </div>
          <div className="ma-text">
            <div className="ma-text-kopf">
              <label className="feld-schild" htmlFor="ma-notiz">
                {t("merkliste.notizIntern")}
              </label>
              <span className="ma-wohin">{t("merkliste.notizInternWas")}</span>
            </div>
            <textarea
              className="feld texte-feld"
              id="ma-notiz"
              rows={2}
              aria-invalid={textZuLang(notiz)}
              placeholder={t("merkliste.notizInternPlatz")}
              value={notiz}
              onChange={(e) => setzeNotiz(e.target.value)}
            />
            <Laengensatz text={notiz} />
          </div>

          <div className={`ma-texte${texteOffen ? " auf" : ""}`}>
            <button
              type="button"
              className="ma-texte-schalter"
              aria-expanded={texteOffen}
              aria-controls="ma-texte-inhalt"
              onClick={() => setzeTexteOffen((x) => !x)}
            >
              <span className="ma-texte-name">{t("merkliste.texteTitel")}</span>
              <span className="ma-texte-stand">
                {t(vortext.trim() || nachtext.trim() ? "merkliste.texteGeaendert" : "merkliste.texteStandard")}
              </span>
              <span className={`ml-ecke${texteOffen ? " offen" : ""}`} aria-hidden="true"><Zeichen name="weiter" /></span>
            </button>
            {texteOffen ? (
              <div className="ma-texte-inhalt" id="ma-texte-inhalt">
                {/* **Die Bausteine hängen an den Belegtexten** (18.09.2026)
                    — dieselbe Liste aus den Stammdaten wie überall. Leer
                    heißt Standard: Er steht als Platzhalter im Feld. */}
                <Belegfeld
                  id="ma-vortext"
                  art="vortext"
                  beleg={belegart}
                  schild="textbausteine.vortext"
                  platz="textbausteine.vortextPlatz"
                  wohin={belegwort}
                  wert={vortext}
                  aendern={setzeVortext}
                />
                <Belegfeld
                  id="ma-nachtext"
                  art="nachtext"
                  beleg={belegart}
                  schild="textbausteine.nachtext"
                  platz="textbausteine.nachtextPlatz"
                  wohin={belegwort}
                  wert={nachtext}
                  aendern={setzeNachtext}
                />
              </div>
            ) : null}
          </div>
        </section>

        {/* ── Summe und Knöpfe ────────────────────────────────────────
            Zwei Knöpfe, und der gedrückte steht vorn. Der andere bleibt
            stehen: Was nicht frei ist, geht als Angebot trotzdem — eine
            Option blockt nicht (Regel 6). */}
        <section className={`ma-teil ma-schluss${bereit ? "" : " still"}`}>

          <div className="ma-summe">
            <span>{t("merkliste.summe")}</span>
            <b>{preiseUnterwegs ? t("allgemein.zahlOffen") : euro(summe)}</b>
          </div>

          {/* Die letzte Bestätigung vor dem verbindlichen Auftrag (Regel 10):
              *„da muss man nochmal bestätigen, der Kunde hat telefonisch
              bestellt."* Sie steht nur am Vermieten — ein Angebot bindet
              niemanden. */}
          {ohneVermieten ? null : (
            <label className="ma-bestaetigen">
              <input
                type="checkbox"
                checked={bestaetigt}
                onChange={(e) => setzeBestaetigt(e.target.checked)}
              />
              <span>{t("merkliste.telefonischBestellt")}</span>
            </label>
          )}

          {fehler ? <p className="ma-fehler" role="alert"><T k={fehler} werte={fehlerDaten} /></p> : null}
          <Nachtraeglichfrage fehler={fehlerRoh} laeuft={laeuft} ja={() => void abschliessen("vermieten", true)} />

          <div className={`ma-taten${art === "angebot" ? " angebot" : ""}`}>
            {ohneVermieten ? null : (
              <Knopf
                art={art === "vermieten" ? "haupt" : ""}
                disabled={!bereit || laeuft}
                aria-disabled={!bestaetigt || undefined}
                onClick={() => void abschliessen("vermieten")}
              >
                {t("merkliste.vermieten")}
              </Knopf>
            )}
            <Knopf
              art={art === "angebot" || nurAngebot ? "haupt" : ""}
              disabled={!bereit || laeuft}
              onClick={() => void abschliessen("angebot")}
            >
              {t("merkliste.angebot")}
            </Knopf>
          </div>
          <p className="ma-fuss">
            {t(art === "vermieten" ? "merkliste.fussVermieten" : "merkliste.fussAngebot")}
          </p>
        </section>
      </div>

      <OrtAnlegen
        offen={ortBlatt !== null}
        kunde={kunde}
        name={ortBlatt ?? ""}
        erster={orte.length === 0}
        schliessen={() => setzeOrtBlatt(null)}
        angelegt={(neu) => { setzeOrtBlatt(null); setzeOrt(neu.name); }}
      />
      {/* **Das Blatt zum Posten auch am Schreibtisch** (Systemprüfung E-02,
          24.09.2026) — für den Bediener: Lisa fand die Auswahl nicht, weil
          sie nur in der Sammlung hinter dem Posten stand und der Abschluss
          erst beim Klick sagte, dass einer fehlt. */}
      <Postenblatt
        offen={offen && blattPosten !== null}
        posten={posten.find((p) => p.id === blattPosten) ?? null}
        kunde={kunde?.nr ?? ""}
        schliessen={() => setzeBlattPosten(null)}
      />
    </Deckblatt>
  );
}
